import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, ArrowRightLeft, BarChart3, ChevronDown, Download, Eye, EyeOff, Pencil, Sparkles, Trash2, Users, X } from 'lucide-react'
import useStore, { PLAN_SLOTS, isComparing, resolveActivePlanKey } from '@/stores/useStore'
import { buildChartData, formatFieldValue } from '@/features/assistant/core/chartConfig.js'
import { CALCULATED_EMPLOYEE_FIELDS } from '@/features/assistant/core/fieldCatalog.js'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CHART_COLORS, PLAN_LIMITS } from '@/lib/constants'
import { buildOwnerPlanComparisonData, buildOwnerSavingsCapacityData } from '@/lib/ownerSavingsCharts.js'
import { formatCurrency, formatPercent, downloadFile } from '@/lib/utils'
import { getEmployeesForMetrics } from '@/lib/calculations'
import { DragSelectWrapper } from '@/components/DragSelectWrapper'
import { Expandable } from '@/components/ui/expandable'
import { useEmployeeTableView } from '@/hooks/useEmployeeTableView'
import SortableHeader from '@/components/employee-table/SortableHeader'
import EmployeeTableControls from '@/components/employee-table/EmployeeTableControls'
import EmptyState from '@/components/employee-table/EmptyState'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

const COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.nhce,
  CHART_COLORS.secondary,
  CHART_COLORS.warning,
  CHART_COLORS.danger,
  CHART_COLORS.muted,
]

const GRID_STROKE = 'hsl(220 13% 88%)'
const TICK_FILL = 'hsl(215 16% 38%)'
const CHART_MARGIN = { top: 12, right: 18, left: 4, bottom: 12 }
const CHART_ANIMATION_PROPS = { isAnimationActive: false }
const TOOLTIP_PROPS = { isAnimationActive: false }

const getEmployeeIds = (datum) => {
  if (!datum) return []
  if (Array.isArray(datum.employeeIds)) return datum.employeeIds
  if (datum.id) return [datum.id]
  return []
}

const getPayload = (entry) => entry?.payload || entry || {}

// Chart-specific columns for the reveal dialog. Each chart passes the
// descriptors matching what it plots, so the dialog table and its CSV carry
// the numbers behind the selection instead of only the generic census set.
// A descriptor: { key, label, type, align, sum?, accessor (sort), cell
// (table), csv (raw export value, must not contain commas) }.
const currencyColumn = (key, label, accessor) => ({
  key,
  label,
  type: 'number',
  align: 'right',
  sum: true,
  accessor,
  cell: (emp) => formatCurrency(Number(accessor(emp)) || 0),
  csv: (emp) => (Number(accessor(emp)) || 0).toFixed(2),
})

const percentColumn = (key, label, accessor) => ({
  key,
  label,
  type: 'number',
  align: 'right',
  accessor,
  cell: (emp) => {
    const value = accessor(emp)
    return value === null || value === undefined ? '-' : formatPercent(value)
  },
  csv: (emp) => {
    const value = accessor(emp)
    return value === null || value === undefined ? '' : Number(value).toFixed(2)
  },
})

const textColumn = (key, label, accessor) => ({
  key,
  label,
  type: 'text',
  align: 'center',
  accessor,
  cell: (emp) => accessor(emp) || '-',
  csv: (emp) => accessor(emp) || '',
})

const PARTICIPATION_LABELS = {
  'non-participant': 'Non-participant',
  participating: 'Participating',
  maxed: 'Maxed out',
}

const GROUP_COLUMNS = {
  pretaxPercent: percentColumn('pretaxPercent', 'Pre-tax %', (emp) => emp.pretaxPercent),
  rothPercent: percentColumn('rothPercent', 'Roth %', (emp) => emp.rothPercent),
  actualPretax: currencyColumn('actualPretax', 'Pre-tax $', (emp) => emp.actualPretax),
  actualRoth: currencyColumn('actualRoth', 'Roth $', (emp) => emp.actualRoth),
  catchup: currencyColumn('catchup', 'Catch-up $', (emp) => emp.catchupContribution),
  afterTax: currencyColumn('afterTax', 'After-tax $', (emp) => emp.afterTaxContribution),
  employerTotal: currencyColumn('employerTotal', 'Employer total', (emp) => emp.employerTotal),
  matchAmount: currencyColumn('matchAmount', 'Match', (emp) => emp.matchAmount),
  necAmount: currencyColumn('necAmount', 'Nonelective', (emp) => emp.necAmount),
  profitSharing: currencyColumn('profitSharing', 'Profit sharing', (emp) => emp.profitSharingAmount),
  cashBalance: currencyColumn('cashBalance', 'Cash balance', (emp) => emp.cashBalanceCredit),
  totalContribution: currencyColumn('totalContribution', 'Total contribution', (emp) => emp.totalContribution),
  balance: currencyColumn('balance', 'Balance', (emp) => emp.balance),
  loanBalance: currencyColumn('loanBalance', 'Loan balance', (emp) => emp.loanBalance),
  loanToBalance: percentColumn('loanToBalance', 'Loan-to-balance', (emp) => (
    emp.balance > 0 ? (emp.loanBalance / emp.balance) * 100 : null
  )),
  participation: textColumn('participation', 'Participation', (emp) => PARTICIPATION_LABELS[emp.participationStatus] || emp.participationStatus),
  catchupEligible: textColumn('catchupEligible', 'Catch-up eligible', (emp) => (emp.catchupEligible ? 'Yes' : 'No')),
  gender: textColumn('gender', 'Gender', (emp) => emp.gender),
  tenure: {
    key: 'tenure',
    label: 'Tenure',
    type: 'number',
    align: 'right',
    accessor: (emp) => emp.tenure?.totalMonths ?? null,
    cell: (emp) => emp.tenure?.formatted || 'Unknown',
    csv: (emp) => emp.tenure?.formatted || '',
  },
  hireDate: textColumn('hireDate', 'Hire date', (emp) => emp.hireDate),
  terminationDate: textColumn('terminationDate', 'Termination date', (emp) => emp.effectiveTerminationDate || emp.terminationDate),
  vestedEmployer: currencyColumn('vestedEmployer', 'Vested employer $', (emp) => emp.vestedEmployerTotal),
  forfeiture: currencyColumn('forfeiture', 'Est. forfeiture', (emp) => emp.estimatedForfeiture),
}

const LOAN_GROUP_COLUMNS = [GROUP_COLUMNS.balance, GROUP_COLUMNS.loanBalance, GROUP_COLUMNS.loanToBalance]

// Fields the dialog already shows for every selection; never duplicated as
// extra columns for custom charts.
const DEFAULT_DIALOG_FIELDS = new Set(['compensation', 'totalDeferralPercent', 'totalEmployeeContribution', 'isHCE', 'status'])

// Column descriptor for a custom-chart field, derived from the shared field
// catalog so user-built charts get dialog columns for whatever they plot.
function fieldGroupColumn(field) {
  const meta = CALCULATED_EMPLOYEE_FIELDS[field]
  if (!meta || DEFAULT_DIALOG_FIELDS.has(field)) return null
  const label = meta.label.charAt(0).toUpperCase() + meta.label.slice(1)
  if (meta.type === 'boolean') return textColumn(field, label, (emp) => (emp[field] ? 'Yes' : 'No'))
  if (meta.type !== 'number') return textColumn(field, label, (emp) => (emp[field] === null || emp[field] === undefined ? '' : String(emp[field])))
  if (field === 'compensation' || /Amount$|Contribution$|balance|employerTotal/i.test(field)) {
    return currencyColumn(field, label, (emp) => emp[field])
  }
  if (meta.label.includes('percentage') || /Percent$/.test(field)) {
    return percentColumn(field, label, (emp) => emp[field])
  }
  return {
    key: field,
    label,
    type: 'number',
    align: 'right',
    accessor: (emp) => Number(emp[field]) || 0,
    cell: (emp) => (emp[field] === null || emp[field] === undefined ? '-' : String(emp[field])),
    csv: (emp) => (emp[field] === null || emp[field] === undefined ? '' : String(emp[field])),
  }
}

function configuredGroupColumns(render) {
  const fields = [render?.groupBy, render?.metric, render?.xKey, render?.yKey]
  const seen = new Set()
  const columns = []
  for (const field of fields) {
    if (!field || field === 'label' || seen.has(field)) continue
    seen.add(field)
    const column = fieldGroupColumn(field)
    if (column) columns.push(column)
  }
  return columns
}

function selectEmployeeGroup(onGroupSelect, { title, description, datum, employeeIds, color, columns }, event) {
  const ids = employeeIds || getEmployeeIds(datum)
  if (!ids.length) return
  onGroupSelect?.({
    title,
    description,
    employeeIds: ids,
    color,
    columns,
    additive: Boolean(event?.shiftKey),
  })
}

// Union of column descriptors when Shift-selections from different charts are
// combined into one group.
function mergeGroupColumns(current = [], incoming = []) {
  const merged = [...current]
  for (const column of incoming) {
    if (!merged.some((existing) => existing.key === column.key)) merged.push(column)
  }
  return merged
}

// The selection state behind every chart's click-through: a plain click opens
// the reveal dialog for that group; Shift-click or Shift-drag accumulates
// groups into a pending multi-selection surfaced by MultiSelectionBar. Each
// committed selection gets a unique id so the dialog body remounts (and resets
// its search/filter/sort + identifier visibility) per selection (Req 6.5).
function useChartGroupSelection() {
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [pendingSelection, setPendingSelection] = useState(null)
  const groupSelectionIdRef = useRef(0)

  const handleGroupSelect = (group) => {
    if (!group) {
      setSelectedGroup(null)
      return
    }
    if (group.additive) {
      setPendingSelection((current) => {
        const employeeIds = Array.from(new Set([...(current?.employeeIds || []), ...(group.employeeIds || [])]))
        return {
          title: 'Multiple chart selections',
          description: `${employeeIds.length} unique employees combined from multiple chart selections.`,
          employeeIds,
          color: group.color || current?.color || CHART_COLORS.primary,
          columns: mergeGroupColumns(current?.columns, group.columns),
          sourceCount: (current?.sourceCount || 0) + 1,
        }
      })
      return
    }
    setPendingSelection(null)
    groupSelectionIdRef.current += 1
    setSelectedGroup({ ...group, id: groupSelectionIdRef.current })
  }

  const viewPendingSelection = () => {
    if (!pendingSelection?.employeeIds?.length) return
    groupSelectionIdRef.current += 1
    setSelectedGroup({ ...pendingSelection, id: groupSelectionIdRef.current })
    setPendingSelection(null)
  }

  return {
    selectedGroup,
    pendingSelection,
    handleGroupSelect,
    viewPendingSelection,
    closeGroup: () => setSelectedGroup(null),
    clearPending: () => setPendingSelection(null),
  }
}

function ChartPlanSwitcher() {
  const activePlanKey = useStore(resolveActivePlanKey)
  const setActivePlanKey = useStore((state) => state.setActivePlanKey)
  const planA = useStore((state) => state.planA)
  const planB = useStore((state) => state.planB)
  const planC = useStore((state) => state.planC)
  const planD = useStore((state) => state.planD)
  const plans = { planA, planB, planC, planD }
  const availablePlans = PLAN_SLOTS.filter((key) => plans[key])
  const activeLetter = activePlanKey.slice(-1).toUpperCase()
  const activeName = plans[activePlanKey]?.name || `Plan ${activeLetter}`

  if (availablePlans.length < 2) return null

  return (
    <Select value={activePlanKey} onValueChange={setActivePlanKey}>
      <SelectTrigger
        aria-label={`Plan shown in charts: ${activeName}`}
        title={`Plan shown in charts: ${activeName}`}
        className="h-7 w-auto min-w-0 gap-1.5 rounded-md bg-background/90 px-2 text-xs shadow-sm"
      >
        <ArrowRightLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="font-semibold">{activeLetter}</span>
      </SelectTrigger>
      <SelectContent align="end">
        {availablePlans.map((key) => {
          const letter = key.slice(-1).toUpperCase()
          const name = plans[key]?.name || `Plan ${letter}`
          return (
            <SelectItem key={key} value={key}>
              <span className="flex items-center gap-2">
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded border bg-muted px-1 text-[10px] font-semibold">
                  {letter}
                </span>
                <span>{name}</span>
              </span>
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function ChartCard({
  id,
  title,
  description,
  children,
  className = '',
  headerAction = null,
  note = null,
  showPlanSwitcher = true,
}) {
  const comparing = useStore(isComparing)

  return (
    <Expandable id={id} title={title} description={description} showHeader={false} className="h-full">
      <Card className={`relative h-full min-w-0 ${className}`}>
        {comparing && showPlanSwitcher && (
          <div className="absolute right-11 top-2 z-20">
            <ChartPlanSwitcher />
          </div>
        )}
        <CardHeader className={`min-w-0 pb-2 ${comparing && showPlanSwitcher ? 'pr-24' : 'pr-10'}`}>
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          {description && <CardDescription className="text-xs">{description}</CardDescription>}
          {(headerAction || note) && (
            <div className="mt-1.5 flex items-center gap-2">
              {headerAction}
              {note && <span className="text-xs font-medium text-muted-foreground">{note}</span>}
            </div>
          )}
        </CardHeader>
        <CardContent className="min-w-0 pt-0">
          {children}
        </CardContent>
      </Card>
    </Expandable>
  )
}

// Charts render exactly one plan design – whichever plan the shared chips have
// active. There is no per-chart A/B diffing: the cross-plan numbers people
// actually compare live in the Summary and Employee tables. `dataA` is simply
// the active plan's slice; the render-prop shape is kept so every chart body
// stays untouched.
function ComparableCard({ id, title, description, dataA, className, children }) {
  return (
    <ChartCard id={id} title={title} description={description} className={className}>
      {children(dataA)}
    </ChartCard>
  )
}

function ChartSection({
  id,
  title,
  description = null,
  children,
  action = null,
  collapsible = false,
  defaultCollapsed = false,
}) {
  const sectionRef = useRef(null)
  const [shouldRender, setShouldRender] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(collapsible && defaultCollapsed)

  useEffect(() => {
    if (shouldRender) return

    const section = sectionRef.current
    if (!section || !window.IntersectionObserver) {
      setShouldRender(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setShouldRender(true)
        observer.disconnect()
      },
      { rootMargin: '700px 0px' }
    )

    observer.observe(section)
    return () => observer.disconnect()
  }, [shouldRender])

  return (
    <section ref={sectionRef} id={id} className="scroll-mt-16 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
          {description && (
            <p className="mt-0.5 max-w-4xl text-xs leading-relaxed text-muted-foreground">{description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {action}
          {collapsible && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={() => setIsCollapsed((current) => !current)}
              aria-expanded={!isCollapsed}
              aria-controls={`${id}-content`}
            >
              {isCollapsed ? 'Show charts' : 'Hide charts'}
              <ChevronDown className={`size-4 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} />
            </Button>
          )}
        </div>
      </div>
      {!isCollapsed && (shouldRender ? (
        <div id={`${id}-content`} className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,24rem),1fr))] gap-4">
          {children}
        </div>
      ) : (
        <div id={`${id}-content`} className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,24rem),1fr))] gap-4">
          <div className="h-[280px] animate-pulse border border-border bg-muted/25" />
          <div className="hidden h-[280px] animate-pulse border border-border bg-muted/25 sm:block" />
        </div>
      ))}
    </section>
  )
}

function CustomTooltip({ active, payload, label, formatter, labelFormatter }) {
  if (!active || !payload || !payload.length) return null

  const datum = payload[0]?.payload

  return (
    <div className="max-w-[18rem] rounded-md border border-border bg-popover p-3 text-xs shadow-sm">
      {label && (
        <p className="mb-1 font-medium text-foreground">
          {labelFormatter ? labelFormatter(label, datum) : label}
        </p>
      )}
      {payload.map((entry, index) => (
        <p key={index} className="leading-relaxed" style={{ color: entry.color }}>
          {entry.name}: {formatter ? formatter(entry.value, entry.name) : entry.value}
        </p>
      ))}
    </div>
  )
}

function ScatterTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null

  const datum = payload[0]?.payload
  if (!datum) return null

  return (
    <div className="max-w-[18rem] rounded-md border border-border bg-popover p-3 text-xs shadow-sm">
      <div className="font-semibold text-foreground">Employee point</div>
      <div className="mt-2 space-y-1">
        {'compensation' in datum && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Compensation</span>
            <span className="font-mono">{formatCurrency(datum.compensation)}</span>
          </div>
        )}
        {'deferralPercent' in datum && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Deferral</span>
            <span className="font-mono">{formatPercent(datum.deferralPercent)}</span>
          </div>
        )}
        {'age' in datum && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Age</span>
            <span className="font-mono">{datum.age}</span>
          </div>
        )}
        {'contribution' in datum && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Contribution</span>
            <span className="font-mono">{formatCurrency(datum.contribution)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// Filter definitions for the group dialog's discrete-valued columns (Req 6.2).
const GROUP_DIALOG_FILTERS = [
  {
    key: 'hce',
    label: 'HCE',
    getValue: (emp) => (emp.isHCE ? 'Yes' : 'No'),
    options: ['Yes', 'No'],
  },
  {
    key: 'status',
    label: 'Status',
    getValue: (emp) => emp.status || 'Active',
    options: ['Active', 'Leave', 'Terminated'],
  },
  {
    key: 'eligibility',
    label: 'Eligibility',
    getValue: (emp) => ({
      eligible: 'Eligible',
      ineligible: 'Ineligible',
      ltpt: 'LTPT (defer only)',
      unknown: 'Unknown',
    })[emp.eligibility?.status] || 'Unknown',
    options: ['Eligible', 'Ineligible', 'LTPT (defer only)', 'Unknown'],
  },
]

function EmployeeGroupDialogBody({ group, employees }) {
  const [showIdentifiers, setShowIdentifiers] = useState(false)
  const ids = useMemo(() => group?.employeeIds || [], [group])
  const employeesById = useMemo(() => new Map(employees.map(employee => [employee.id, employee])), [employees])
  const selectedEmployees = useMemo(
    () => ids.map(id => employeesById.get(id)).filter(Boolean),
    [ids, employeesById]
  )
  const missingIds = useMemo(
    () => ids.filter(id => !employeesById.has(id)),
    [ids, employeesById]
  )

  // Chart-specific columns attached to this selection (see GROUP_COLUMNS).
  const extraColumns = useMemo(() => group?.columns || [], [group])

  // Sort descriptors. The id column is only sortable while identifiers are
  // revealed (Req 6.4); the numeric/HCE/Status columns are always sortable.
  const columns = useMemo(() => ({
    id: { type: 'text', accessor: (emp) => emp.id },
    hce: { type: 'text', accessor: (emp) => (emp.isHCE ? 'Yes' : 'No') },
    status: { type: 'text', accessor: (emp) => emp.status || 'Active' },
    eligibility: { type: 'text', accessor: (emp) => emp.eligibility?.status || 'unknown' },
    compensation: { type: 'number', accessor: (emp) => emp.compensation },
    deferral: { type: 'number', accessor: (emp) => emp.totalDeferralPercent },
    employeeTotal: { type: 'number', accessor: (emp) => emp.totalEmployeeContribution },
    ...Object.fromEntries(extraColumns.map((column) => [column.key, { type: column.type, accessor: column.accessor }])),
  }), [extraColumns])

  // Search text respects identifier visibility: while hidden, never include id
  // or name so hidden values can never be matched (Req 6.4).
  const getSearchText = useMemo(
    () => (emp) => (
      showIdentifiers
        ? `${emp.id ?? ''} ${emp.name ?? ''} ${emp.allocationGroupLabel ?? ''} ${emp.age ?? ''} ${emp.isHCE ? 'Yes' : 'No'} ${emp.status || 'Active'}`
        : `${emp.allocationGroupLabel ?? ''} ${emp.isHCE ? 'Yes' : 'No'} ${emp.status || 'Active'}`
    ),
    [showIdentifiers]
  )

  // Force the view to recompute when identifier visibility toggles so that the
  // active search re-evaluates against the now-visible/hidden fields.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when identifier visibility toggles so search re-evaluates
  const rows = useMemo(() => selectedEmployees, [selectedEmployees, showIdentifiers])

  const {
    viewRows,
    search,
    setSearch,
    activeFilters,
    setFilter,
    clearAll,
    sort,
    toggleSort,
    totalCount,
    activeCount,
    isActive,
  } = useEmployeeTableView({
    rows,
    getSearchText,
    filters: GROUP_DIALOG_FILTERS,
    columns,
  })

  const totalComp = selectedEmployees.reduce((sum, employee) => sum + employee.compensation, 0)
  const avgDeferral = selectedEmployees.length > 0
    ? selectedEmployees.reduce((sum, employee) => sum + employee.totalDeferralPercent, 0) / selectedEmployees.length
    : 0
  const totalEmployeeContributions = selectedEmployees.reduce((sum, employee) => sum + employee.totalEmployeeContribution, 0)

  // Number of columns in the table body, used for the empty-state row colSpan.
  const columnCount = (showIdentifiers ? 9 : 7) + extraColumns.length

  const handleExportCsv = () => {
    if (viewRows.length === 0) return
    // The fixed header set already covers employer total and gender.
    const csvExtraColumns = extraColumns.filter((column) => column.key !== 'employerTotal' && column.key !== 'gender')
    const headers = [
      'ID', 'Name', 'Allocation Group', 'Compensation', 'Deferral %', 'Employee Total', 'Employer Total', 'HCE', 'Status', 'Eligibility', 'Age', 'Gender',
      ...csvExtraColumns.map((column) => column.label),
    ]
    const csvRows = viewRows.map(emp => [
      emp.id,
      emp.name || '',
      emp.allocationGroupLabel || '',
      emp.compensation.toFixed(2),
      emp.totalDeferralPercent.toFixed(2),
      emp.totalEmployeeContribution.toFixed(2),
      emp.employerTotal.toFixed(2),
      emp.isHCE ? 'Yes' : 'No',
      emp.status || 'Active',
      ({
        eligible: 'Eligible',
        ineligible: 'Ineligible',
        ltpt: 'LTPT (defer only)',
        unknown: 'Unknown',
      })[emp.eligibility?.status] || 'Unknown',
      emp.age !== null ? emp.age : '',
      emp.gender || '',
      ...csvExtraColumns.map((column) => column.csv(emp)),
    ])
    const csv = [headers.join(','), ...csvRows.map(r => r.join(','))].join('\n')
    const filename = `401k-selection-${(group?.title || 'employees').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`
    downloadFile(csv, filename, 'text/csv')
  }

  return (
    <>
      <DialogHeader className="pr-8">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <DialogTitle>{group?.title || 'Employees'}</DialogTitle>
            <DialogDescription>
              {group?.description || `${ids.length} employee${ids.length === 1 ? '' : 's'} in this selection.`}
            </DialogDescription>
          </div>
          {ids.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 shrink-0"
                onClick={handleExportCsv}
              >
                <Download className="h-3.5 w-3.5" />
                CSV
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 shrink-0"
                onClick={() => setShowIdentifiers((value) => !value)}
              >
                {showIdentifiers ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showIdentifiers ? 'Hide Employee Details' : 'Show Employee Details'}
              </Button>
            </div>
          )}
        </div>
      </DialogHeader>

      <div className="flex min-h-0 flex-col gap-4 overflow-hidden">
        <div className="grid gap-2 sm:grid-cols-3">
          <div className="border border-border bg-muted/25 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Employees</div>
            <div className="font-mono text-base font-semibold">{ids.length}</div>
          </div>
          <div className="border border-border bg-muted/25 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Avg deferral</div>
            <div className="font-mono text-base font-semibold">{formatPercent(avgDeferral)}</div>
          </div>
          <div className="border border-border bg-muted/25 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Employee total</div>
            <div className="font-mono text-base font-semibold">{formatCurrency(totalEmployeeContributions)}</div>
          </div>
        </div>

        {ids.length > 0 && (
          <EmployeeTableControls
            search={search}
            onSearchChange={setSearch}
            filters={GROUP_DIALOG_FILTERS}
            activeFilters={activeFilters}
            onFilterChange={setFilter}
            onClear={clearAll}
            isActive={isActive}
            activeCount={activeCount}
            totalCount={totalCount}
            searchPlaceholder={showIdentifiers ? 'Search by ID, name, class, HCE, or status…' : 'Search by class, HCE, or status…'}
          />
        )}

        {showIdentifiers && (
          <div className="flex max-h-20 flex-wrap gap-1 overflow-auto rounded border border-border bg-background p-2">
            {ids.map(id => (
              <span key={id} className="inline-flex border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[11px]">
                {id}
              </span>
            ))}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto rounded border border-border">
          <table className="data-table data-table-output text-xs">
            <thead className="sticky top-0 z-10">
              <tr>
                {showIdentifiers ? (
                  <SortableHeader
                    columnKey="id"
                    active={sort.key === 'id'}
                    dir={sort.key === 'id' ? sort.dir : null}
                    onToggle={toggleSort}
                    className="sticky-col sticky-employee-id min-w-[110px]"
                  >
                    Employee
                  </SortableHeader>
                ) : (
                  <th className="sticky-col sticky-employee-id min-w-[110px] whitespace-nowrap">Employee</th>
                )}
                {showIdentifiers && <th className="sticky-col sticky-employee-name whitespace-nowrap">Name</th>}
                {showIdentifiers && <th className="whitespace-nowrap text-right">Age</th>}
                <SortableHeader
                  columnKey="compensation"
                  active={sort.key === 'compensation'}
                  dir={sort.key === 'compensation' ? sort.dir : null}
                  align="right"
                  onToggle={toggleSort}
                >
                  Compensation
                </SortableHeader>
                <SortableHeader
                  columnKey="deferral"
                  active={sort.key === 'deferral'}
                  dir={sort.key === 'deferral' ? sort.dir : null}
                  align="right"
                  onToggle={toggleSort}
                >
                  Deferral %
                </SortableHeader>
                <SortableHeader
                  columnKey="employeeTotal"
                  active={sort.key === 'employeeTotal'}
                  dir={sort.key === 'employeeTotal' ? sort.dir : null}
                  align="right"
                  onToggle={toggleSort}
                >
                  Employee total
                </SortableHeader>
                {extraColumns.map((column) => (
                  <SortableHeader
                    key={column.key}
                    columnKey={column.key}
                    active={sort.key === column.key}
                    dir={sort.key === column.key ? sort.dir : null}
                    align={column.align === 'right' ? 'right' : undefined}
                    onToggle={toggleSort}
                  >
                    {column.label}
                  </SortableHeader>
                ))}
                <SortableHeader
                  columnKey="hce"
                  active={sort.key === 'hce'}
                  dir={sort.key === 'hce' ? sort.dir : null}
                  onToggle={toggleSort}
                >
                  HCE
                </SortableHeader>
                <SortableHeader
                  columnKey="status"
                  active={sort.key === 'status'}
                  dir={sort.key === 'status' ? sort.dir : null}
                  onToggle={toggleSort}
                >
                  Status
                </SortableHeader>
                <SortableHeader
                  columnKey="eligibility"
                  active={sort.key === 'eligibility'}
                  dir={sort.key === 'eligibility' ? sort.dir : null}
                  onToggle={toggleSort}
                >
                  Eligibility
                </SortableHeader>
              </tr>
            </thead>
            <tbody>
              {viewRows.length === 0 && isActive && (
                <EmptyState colSpan={columnCount} onClear={clearAll} />
              )}
              {viewRows.map((employee, index) => (
                <tr key={employee.id}>
                  <td className="sticky-col sticky-employee-id font-mono">
                    {showIdentifiers ? employee.id : `Employee ${index + 1}`}
                  </td>
                  {showIdentifiers && <td className="sticky-col sticky-employee-name">{employee.name || '-'}</td>}
                  {showIdentifiers && <td className="text-right font-mono">{employee.age ?? 'N/A'}</td>}
                  <td className="text-right font-mono">{formatCurrency(employee.compensation)}</td>
                  <td className="text-right font-mono">{formatPercent(employee.totalDeferralPercent)}</td>
                  <td className="text-right font-mono">{formatCurrency(employee.totalEmployeeContribution)}</td>
                  {extraColumns.map((column) => (
                    <td key={column.key} className={column.align === 'right' ? 'text-right font-mono' : 'text-center'}>
                      {column.cell(employee)}
                    </td>
                  ))}
                  <td className="text-center">{employee.isHCE ? 'Yes' : 'No'}</td>
                  <td className="text-center">{employee.status || 'Active'}</td>
                  <td className="text-center">{
                    ({
                      eligible: 'Eligible',
                      ineligible: 'Ineligible',
                      ltpt: 'LTPT (defer only)',
                      unknown: 'Unknown',
                    })[employee.eligibility?.status] || 'Unknown'
                  }</td>
                </tr>
              ))}
              {missingIds.map(id => (
                <tr key={id}>
                  <td className="sticky-col sticky-employee-id font-mono">{showIdentifiers ? id : 'Unavailable'}</td>
                  {/* The message can't live in a cell that spans the frozen name
                      column, or it would slide through it while panning. */}
                  {showIdentifiers && <td className="sticky-col sticky-employee-name" />}
                  <td colSpan={(showIdentifiers ? 7 : 6) + extraColumns.length} className="text-muted-foreground">Employee details unavailable</td>
                </tr>
              ))}
            </tbody>
            {selectedEmployees.length > 0 && (
              <tfoot className="bg-muted/50 font-medium">
                <tr>
                  {showIdentifiers && <td className="sticky-col sticky-employee-id" />}
                  {showIdentifiers && <td className="sticky-col sticky-employee-name" />}
                  <td className="text-right">Selected totals:</td>
                  <td className="text-right font-mono">{formatCurrency(totalComp)}</td>
                  <td className="text-right font-mono">{formatPercent(avgDeferral)}</td>
                  <td className="text-right font-mono">{formatCurrency(totalEmployeeContributions)}</td>
                  {extraColumns.map((column) => (
                    <td key={column.key} className="text-right font-mono">
                      {column.sum
                        ? formatCurrency(selectedEmployees.reduce((sum, emp) => sum + (Number(column.accessor(emp)) || 0), 0))
                        : ''}
                    </td>
                  ))}
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  )
}

function EmployeeGroupDialog({ group, employees, onClose }) {
  return (
    <Dialog open={Boolean(group)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="grid max-h-[86vh] max-w-4xl grid-rows-[auto_minmax(0,1fr)] overflow-hidden">
        {/* Remount per selection so search/filter/sort (and identifier visibility)
            reset to their default empty state for each new selection (Req 6.5). */}
        <EmployeeGroupDialogBody
          key={group?.id}
          group={group}
          employees={employees}
        />
      </DialogContent>
    </Dialog>
  )
}

function DeferralDistributionChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />

  const dialogColumns = [GROUP_COLUMNS.pretaxPercent, GROUP_COLUMNS.rothPercent]

  return (
    <DragSelectWrapper
      chartData={data}
      chartType="bar"
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => getEmployeeIds(item)}
      getTitle={(items) => `Deferral rates: ${items.map(i => i.range).join(', ')}`}
      getDescription={(items) => `${items.reduce((s, i) => s + (i.count || 0), 0)} employees across selected ranges.`}
      groupColumns={dialogColumns}
      color={CHART_COLORS.primary}
      height={280}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis dataKey="range" tick={{ fontSize: 11, fill: TICK_FILL }} height={42} interval={0} />
        <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={42} allowDecimals={false} />
        <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip />} />
        <Bar
          dataKey="count"
          fill={CHART_COLORS.primary}
          radius={[3, 3, 0, 0]}
          name="Employees"
          className="cursor-pointer"
          {...CHART_ANIMATION_PROPS}
          onClick={(entry, _index, event) => {
            const datum = getPayload(entry)
            selectEmployeeGroup(onGroupSelect, {
              title: `Deferral rate ${datum.range}`,
              description: `${datum.count} employees in this deferral range.`,
              datum,
              color: CHART_COLORS.primary,
              columns: dialogColumns,
            }, event)
          }}
        />
      </BarChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function CompensationDistributionChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />

  const dialogColumns = [GROUP_COLUMNS.employerTotal, GROUP_COLUMNS.totalContribution]

  return (
    <DragSelectWrapper
      chartData={data}
      chartType="bar"
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => getEmployeeIds(item)}
      getTitle={(items) => `Compensation: ${items.map(i => i.range).join(', ')}`}
      getDescription={(items) => `${items.reduce((s, i) => s + (i.count || 0), 0)} employees across selected compensation bands.`}
      groupColumns={dialogColumns}
      color={CHART_COLORS.secondary}
      height={280}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis dataKey="range" tick={{ fontSize: 10, fill: TICK_FILL }} height={48} interval={0} angle={-18} textAnchor="end" />
        <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={42} allowDecimals={false} />
        <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip />} />
        <Bar
          dataKey="count"
          fill={CHART_COLORS.secondary}
          radius={[3, 3, 0, 0]}
          name="Employees"
          className="cursor-pointer"
          {...CHART_ANIMATION_PROPS}
          onClick={(entry, _index, event) => {
            const datum = getPayload(entry)
            selectEmployeeGroup(onGroupSelect, {
              title: `Compensation ${datum.range}`,
              description: `${datum.count} employees in this compensation band.`,
              datum,
              color: CHART_COLORS.secondary,
              columns: dialogColumns,
            }, event)
          }}
        />
      </BarChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function AgeContributionsChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />

  const hasAfterTax = data.some((d) => (d.avgAfterTax || 0) > 0)

  const dialogColumns = [
    GROUP_COLUMNS.actualPretax,
    GROUP_COLUMNS.actualRoth,
    GROUP_COLUMNS.catchup,
    ...(hasAfterTax ? [GROUP_COLUMNS.afterTax] : []),
  ]

  const handleClick = (entry, _index, event) => {
    const datum = getPayload(entry)
    selectEmployeeGroup(onGroupSelect, {
      title: `Contributions for ${datum.ageBand}`,
      description: `${datum.count} employees in this age band.`,
      datum,
      color: CHART_COLORS.preTax,
      columns: dialogColumns,
    }, event)
  }

  return (
    <DragSelectWrapper
      chartData={data}
      chartType="bar"
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => getEmployeeIds(item)}
      getTitle={(items) => `Contributions: ${items.map(i => i.ageBand).join(', ')}`}
      getDescription={(items) => `${items.reduce((s, i) => s + (i.count || 0), 0)} employees across selected age bands.`}
      groupColumns={dialogColumns}
      color={CHART_COLORS.preTax}
      height={310}
      chartMargin={{ ...CHART_MARGIN, bottom: 4 }}
    >
      <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ ...CHART_MARGIN, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis dataKey="ageBand" tick={{ fontSize: 11, fill: TICK_FILL }} height={36} interval={0} />
        <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={48} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
        <Tooltip
          {...TOOLTIP_PROPS}
          content={<CustomTooltip formatter={(value) => formatCurrency(value)} />}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="avgPretax" stackId="a" fill={CHART_COLORS.preTax} name="Pre-tax" className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
        <Bar dataKey="avgRoth" stackId="a" fill={CHART_COLORS.roth} name="Roth" className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
        <Bar dataKey="avgCatchup" stackId="a" fill={CHART_COLORS.catchup} name="Catch-up" radius={hasAfterTax ? undefined : [3, 3, 0, 0]} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
        {hasAfterTax && (
          <Bar dataKey="avgAfterTax" stackId="a" fill={CHART_COLORS.afterTax} name="After-tax" radius={[3, 3, 0, 0]} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
        )}
      </BarChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function EmployerByAgeBandChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />

  // Cash-balance pay credits stack on top of DC employer money. The indigo is
  // deliberately close to the Roth/secondary purples elsewhere, so a legend is
  // required whenever the cash-balance segment renders.
  const hasCashBalance = data.some((d) => (d.avgCashBalance || 0) > 0)

  const dialogColumns = [
    GROUP_COLUMNS.matchAmount,
    GROUP_COLUMNS.necAmount,
    GROUP_COLUMNS.profitSharing,
    ...(hasCashBalance ? [GROUP_COLUMNS.cashBalance] : []),
    GROUP_COLUMNS.employerTotal,
  ]

  return (
    <DragSelectWrapper
      chartData={data}
      chartType="bar"
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => getEmployeeIds(item)}
      getTitle={(items) => `Employer contribution: ${items.map(i => i.ageBand).join(', ')}`}
      getDescription={(items) => `${items.reduce((s, i) => s + (i.count || 0), 0)} employees across selected age bands.`}
      groupColumns={dialogColumns}
      color={CHART_COLORS.employer}
      height={290}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis dataKey="ageBand" tick={{ fontSize: 11, fill: TICK_FILL }} height={36} interval={0} />
        <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={48} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
        <Tooltip
          {...TOOLTIP_PROPS}
          content={<CustomTooltip formatter={(value) => formatCurrency(value)} />}
        />
        {hasCashBalance && <Legend wrapperStyle={{ fontSize: 11 }} />}
        <Bar
          dataKey="avgEmployer"
          stackId="a"
          fill={CHART_COLORS.employer}
          radius={hasCashBalance ? undefined : [3, 3, 0, 0]}
          name={hasCashBalance ? 'Avg DC employer' : 'Avg employer contribution'}
          className="cursor-pointer"
          {...CHART_ANIMATION_PROPS}
          onClick={(entry, _index, event) => {
            const datum = getPayload(entry)
            selectEmployeeGroup(onGroupSelect, {
              title: `Employer contribution for ${datum.ageBand}`,
              description: `${datum.count} employees in this age band.`,
              datum,
              color: CHART_COLORS.employer,
              columns: dialogColumns,
            }, event)
          }}
        />
        {hasCashBalance && (
          <Bar
            dataKey="avgCashBalance"
            stackId="a"
            fill={CHART_COLORS.cashBalance}
            radius={[3, 3, 0, 0]}
            name="Avg cash balance"
            className="cursor-pointer"
            {...CHART_ANIMATION_PROPS}
            onClick={(entry, _index, event) => {
              const datum = getPayload(entry)
              selectEmployeeGroup(onGroupSelect, {
                title: `Cash balance credit for ${datum.ageBand}`,
                description: `${datum.count} employees in this age band.`,
                datum,
                color: CHART_COLORS.cashBalance,
                columns: dialogColumns,
              }, event)
            }}
          />
        )}
      </BarChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function PieLegend({ data, colors, onSelect }) {
  const total = data.reduce((sum, item) => sum + item.value, 0)

  return (
    <div className="grid gap-2 sm:grid-cols-3">
      {data.map((item, index) => {
        const color = colors[item.name] || COLORS[index % COLORS.length]
        const pct = total > 0 ? (item.value / total) * 100 : 0
        return (
          <button
            key={item.name}
            type="button"
            className="flex min-w-0 items-center gap-2 border border-border bg-muted/25 px-2 py-2 text-left text-xs transition-colors hover:bg-muted/50"
            onClick={(event) => onSelect(item, color, event)}
          >
            <span className="h-2.5 w-2.5 shrink-0" style={{ backgroundColor: color }} />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-foreground">{item.name}</span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {item.value} / {pct.toFixed(1)}%
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

function ParticipationStatusChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />
  
  const COLORS_MAP = {
    'Non-participant': CHART_COLORS.inactive,
    'Participating': CHART_COLORS.primary,
    'Maxed out': CHART_COLORS.success,
  }

  const dialogColumns = [GROUP_COLUMNS.participation]

  const handleSelect = (item, color, event) => {
    const datum = getPayload(item)
    selectEmployeeGroup(onGroupSelect, {
      title: datum.name,
      description: `${datum.value} employees with participation status: ${datum.name}.`,
      datum,
      color,
      columns: dialogColumns,
    }, event)
  }

  return (
    <div className="space-y-3">
      <DragSelectWrapper
        chartData={data}
        chartType="pie"
        onGroupSelect={onGroupSelect}
        getEmployeeIds={(item) => getEmployeeIds(item)}
        getTitle={(items) => `Participation: ${items.map((item) => item.name).join(', ')}`}
        getDescription={(items) => `${new Set(items.flatMap((item) => getEmployeeIds(item))).size} employees across selected participation statuses.`}
        groupColumns={dialogColumns}
        color={CHART_COLORS.primary}
        height={220}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="52%"
              outerRadius="78%"
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
              className="cursor-pointer"
              {...CHART_ANIMATION_PROPS}
              onClick={(entry, _index, event) => {
                const datum = getPayload(entry)
                handleSelect(datum, COLORS_MAP[datum.name] || COLORS[0], event)
              }}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS_MAP[entry.name] || COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </DragSelectWrapper>
      <PieLegend data={data} colors={COLORS_MAP} onSelect={handleSelect} />
    </div>
  )
}

function CatchupUsageChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />
  
  const COLORS_MAP = {
    'Not eligible': CHART_COLORS.inactive,
    'Eligible, not used': CHART_COLORS.warning,
    'Using catch-up': CHART_COLORS.success,
  }

  const dialogColumns = [GROUP_COLUMNS.catchupEligible, GROUP_COLUMNS.catchup]

  const handleSelect = (item, color, event) => {
    const datum = getPayload(item)
    selectEmployeeGroup(onGroupSelect, {
      title: datum.name,
      description: `${datum.value} employees in this catch-up category.`,
      datum,
      color,
      columns: dialogColumns,
    }, event)
  }

  return (
    <div className="space-y-3">
      <DragSelectWrapper
        chartData={data}
        chartType="pie"
        onGroupSelect={onGroupSelect}
        getEmployeeIds={(item) => getEmployeeIds(item)}
        getTitle={(items) => `Catch-up: ${items.map((item) => item.name).join(', ')}`}
        getDescription={(items) => `${new Set(items.flatMap((item) => getEmployeeIds(item))).size} employees across selected catch-up categories.`}
        groupColumns={dialogColumns}
        color={CHART_COLORS.success}
        height={220}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="52%"
              outerRadius="78%"
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
              className="cursor-pointer"
              {...CHART_ANIMATION_PROPS}
              onClick={(entry, _index, event) => {
                const datum = getPayload(entry)
                handleSelect(datum, COLORS_MAP[datum.name] || COLORS[0], event)
              }}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS_MAP[entry.name] || COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </DragSelectWrapper>
      <PieLegend data={data} colors={COLORS_MAP} onSelect={handleSelect} />
    </div>
  )
}

function HCEComparisonChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />

  const dialogColumns = [GROUP_COLUMNS.participation]

  const selectHce = (entry, _index, event) => {
    const datum = getPayload(entry)
    const employeeIds = datum.hceEmployeeIds || []
    selectEmployeeGroup(onGroupSelect, {
      title: `HCE: ${datum.category}`,
      description: `${employeeIds.length} HCE employees represented in this metric.`,
      employeeIds,
      color: CHART_COLORS.hce,
      columns: dialogColumns,
    }, event)
  }
  const selectNhce = (entry, _index, event) => {
    const datum = getPayload(entry)
    const employeeIds = datum.nhceEmployeeIds || []
    selectEmployeeGroup(onGroupSelect, {
      title: `NHCE: ${datum.category}`,
      description: `${employeeIds.length} NHCE employees represented in this metric.`,
      employeeIds,
      color: CHART_COLORS.nhce,
      columns: dialogColumns,
    }, event)
  }

  return (
    <DragSelectWrapper
      chartData={data}
      chartType="bar"
      onGroupSelect={onGroupSelect}
      groupColumns={dialogColumns}
      getEmployeeIds={(item) => [...(item.hceEmployeeIds || []), ...(item.nhceEmployeeIds || [])]}
      getTitle={(items) => `HCE vs NHCE: ${items.map(i => i.category).join(', ')}`}
      getDescription={(items) => {
        const hceIds = new Set(items.flatMap(i => i.hceEmployeeIds || []))
        const nhceIds = new Set(items.flatMap(i => i.nhceEmployeeIds || []))
        return `${hceIds.size + nhceIds.size} employees (${hceIds.size} HCE, ${nhceIds.size} NHCE) across selected categories.`
      }}
      color={CHART_COLORS.hce}
      height={310}
      chartMargin={{ ...CHART_MARGIN, bottom: 18 }}
    >
      <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ ...CHART_MARGIN, bottom: 18 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis dataKey="category" tick={{ fontSize: 10, fill: TICK_FILL }} height={56} interval={0} angle={-16} textAnchor="end" />
        <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={42} />
        <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip formatter={(value) => value.toFixed(2)} />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="HCE" fill={CHART_COLORS.hce} radius={[3, 3, 0, 0]} name="HCE" className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={selectHce} />
        <Bar dataKey="NHCE" fill={CHART_COLORS.nhce} radius={[3, 3, 0, 0]} name="NHCE" className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={selectNhce} />
      </BarChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function CompVsDeferralScatter({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />

  const hceData = data.filter(d => d.isHCE)
  const nhceData = data.filter(d => !d.isHCE)
  const dialogColumns = [GROUP_COLUMNS.pretaxPercent, GROUP_COLUMNS.rothPercent]
  const handleClick = (entry, _index, event) => {
    const datum = getPayload(entry)
    selectEmployeeGroup(onGroupSelect, {
      title: 'Employee point',
      description: `Compensation: ${formatCurrency(datum.compensation)}, Deferral: ${formatPercent(datum.deferralPercent)}.`,
      datum,
      color: datum.isHCE ? CHART_COLORS.hce : CHART_COLORS.nhce,
      columns: dialogColumns,
    }, event)
  }

  return (
    <DragSelectWrapper
      chartType="scatter"
      chartData={data}
      scatterDataSets={[nhceData, hceData]}
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => [item.id]}
      getTitle={(items) => `${items.length} employee${items.length === 1 ? '' : 's'} selected`}
      getDescription={(items) => {
        const hceCount = items.filter(i => i.isHCE).length
        const nhceCount = items.length - hceCount
        return `${items.length} employees (${hceCount} HCE, ${nhceCount} NHCE) in selected region.`
      }}
      groupColumns={dialogColumns}
      color={CHART_COLORS.primary}
      height={320}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis 
          type="number" 
          dataKey="compensation" 
          name="Compensation"
          tick={{ fontSize: 11, fill: TICK_FILL }}
          tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`}
          domain={['auto', 'auto']}
          height={36}
        />
        <YAxis 
          type="number" 
          dataKey="deferralPercent" 
          name="Deferral %"
          tick={{ fontSize: 11, fill: TICK_FILL }}
          tickFormatter={(v) => `${v}%`}
          domain={[0, 'auto']}
          width={44}
        />
        <Tooltip {...TOOLTIP_PROPS} content={<ScatterTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Scatter name="NHCE" data={nhceData} fill={CHART_COLORS.nhce} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
        <Scatter name="HCE" data={hceData} fill={CHART_COLORS.hce} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
      </ScatterChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function AgeVsContributionScatter({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />

  const eligibleData = data.filter(d => d.catchupEligible)
  const notEligibleData = data.filter(d => !d.catchupEligible)
  const dialogColumns = [GROUP_COLUMNS.catchupEligible, GROUP_COLUMNS.catchup]
  const handleClick = (entry, _index, event) => {
    const datum = getPayload(entry)
    selectEmployeeGroup(onGroupSelect, {
      title: 'Employee point',
      description: `Age: ${datum.age ?? 'N/A'}, Contribution: ${formatCurrency(datum.contribution)}.`,
      datum,
      color: datum.catchupEligible ? CHART_COLORS.success : CHART_COLORS.primary,
      columns: dialogColumns,
    }, event)
  }

  return (
    <DragSelectWrapper
      chartType="scatter"
      chartData={data}
      scatterDataSets={[notEligibleData, eligibleData]}
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => [item.id]}
      getTitle={(items) => `${items.length} employee${items.length === 1 ? '' : 's'} selected`}
      getDescription={(items) => {
        const eligibleCount = items.filter(i => i.catchupEligible).length
        const notEligibleCount = items.length - eligibleCount
        return `${items.length} employees (${eligibleCount} catch-up eligible, ${notEligibleCount} under 50) in selected region.`
      }}
      groupColumns={dialogColumns}
      color={CHART_COLORS.primary}
      height={320}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis 
          type="number" 
          dataKey="age" 
          name="Age"
          tick={{ fontSize: 11, fill: TICK_FILL }}
          domain={['auto', 'auto']}
          height={36}
        />
        <YAxis 
          type="number" 
          dataKey="contribution" 
          name="Contribution"
          tick={{ fontSize: 11, fill: TICK_FILL }}
          tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`}
          domain={[0, 'auto']}
          width={48}
        />
        <Tooltip {...TOOLTIP_PROPS} content={<ScatterTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Scatter name="Under 50" data={notEligibleData} fill={CHART_COLORS.primary} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
        <Scatter name="Catch-up eligible" data={eligibleData} fill={CHART_COLORS.success} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
      </ScatterChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function LoanScatterTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null

  const datum = payload[0]?.payload
  if (!datum) return null

  return (
    <div className="max-w-[18rem] rounded-md border border-border bg-popover p-3 text-xs shadow-sm">
      <div className="font-semibold text-foreground">Employee point</div>
      <div className="mt-2 space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Balance</span>
          <span className="font-mono">{formatCurrency(datum.balance)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Loan Balance</span>
          <span className="font-mono">{formatCurrency(datum.loanBalance)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Loan-to-Balance</span>
          <span className="font-mono">{(datum.loanToBalanceRatio * 100).toFixed(1)}%</span>
        </div>
        {'age' in datum && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Age</span>
            <span className="font-mono">{datum.age}</span>
          </div>
        )}
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">HCE</span>
          <span className="font-mono">{datum.isHCE ? 'Yes' : 'No'}</span>
        </div>
      </div>
    </div>
  )
}

function LoanByAgeBandChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No loan data available" />

  return (
    <DragSelectWrapper
      chartData={data}
      chartType="bar"
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => getEmployeeIds(item)}
      getTitle={(items) => `Loans: ${items.map(i => i.ageBand).join(', ')}`}
      getDescription={(items) => `${items.reduce((s, i) => s + (i.count || 0), 0)} employees with loans across selected age bands.`}
      groupColumns={LOAN_GROUP_COLUMNS}
      color={CHART_COLORS.loan}
      height={280}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis dataKey="ageBand" tick={{ fontSize: 11, fill: TICK_FILL }} height={36} interval={0} />
        <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={48} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
        <Tooltip
          {...TOOLTIP_PROPS}
          content={<CustomTooltip formatter={(value) => formatCurrency(value)} />}
        />
        <Bar
          dataKey="totalLoan"
          fill={CHART_COLORS.loan}
          radius={[3, 3, 0, 0]}
          name="Total Loan Amount"
          className="cursor-pointer"
          {...CHART_ANIMATION_PROPS}
          onClick={(entry, _index, event) => {
            const datum = getPayload(entry)
            selectEmployeeGroup(onGroupSelect, {
              title: `Loans for ${datum.ageBand}`,
              description: `${datum.count} employees with loans in this age band.`,
              datum,
              color: CHART_COLORS.loan,
              columns: LOAN_GROUP_COLUMNS,
            }, event)
          }}
        />
      </BarChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function LoanTakersTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null

  const datum = payload[0]?.payload
  if (!datum) return null

  return (
    <div className="max-w-[18rem] rounded-md border border-border bg-popover p-3 text-xs shadow-sm">
      <div className="font-semibold text-foreground">{datum.ageBand}</div>
      <div className="mt-2 space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Employees with loans</span>
          <span className="font-mono">{datum.count} of {datum.bandTotal}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Share of age group</span>
          <span className="font-mono">{(datum.usageRate || 0).toFixed(1)}%</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Avg loan</span>
          <span className="font-mono">{formatCurrency(datum.avgLoan)}</span>
        </div>
      </div>
    </div>
  )
}

function LoanTakersByAgeChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No loan data available" />

  return (
    <DragSelectWrapper
      chartData={data}
      chartType="bar"
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => getEmployeeIds(item)}
      getTitle={(items) => `Loans: ${items.map(i => i.ageBand).join(', ')}`}
      getDescription={(items) => `${items.reduce((s, i) => s + (i.count || 0), 0)} employees with loans across selected age groups.`}
      groupColumns={LOAN_GROUP_COLUMNS}
      color={CHART_COLORS.loan}
      height={280}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis dataKey="ageBand" tick={{ fontSize: 11, fill: TICK_FILL }} height={36} interval={0} />
        <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={42} allowDecimals={false} />
        <Tooltip {...TOOLTIP_PROPS} content={<LoanTakersTooltip />} />
        <Bar
          dataKey="count"
          fill={CHART_COLORS.loan}
          radius={[3, 3, 0, 0]}
          name="Employees with loans"
          className="cursor-pointer"
          {...CHART_ANIMATION_PROPS}
          onClick={(entry, _index, event) => {
            const datum = getPayload(entry)
            selectEmployeeGroup(onGroupSelect, {
              title: `Loans for ${datum.ageBand}`,
              description: `${datum.count} of ${datum.bandTotal} employees in this age group have an outstanding loan.`,
              datum,
              color: CHART_COLORS.loan,
              columns: LOAN_GROUP_COLUMNS,
            }, event)
          }}
        />
      </BarChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function LoanVsBalanceScatter({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No loan data available" />

  const hceData = data.filter(d => d.isHCE)
  const nhceData = data.filter(d => !d.isHCE)
  const handleClick = (entry, _index, event) => {
    const datum = getPayload(entry)
    selectEmployeeGroup(onGroupSelect, {
      title: 'Employee point',
      description: `Balance: ${formatCurrency(datum.balance)}, Loan: ${formatCurrency(datum.loanBalance)}`,
      datum,
      color: datum.isHCE ? CHART_COLORS.hce : CHART_COLORS.nhce,
      columns: LOAN_GROUP_COLUMNS,
    }, event)
  }

  return (
    <DragSelectWrapper
      chartType="scatter"
      chartData={data}
      scatterDataSets={[nhceData, hceData]}
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => [item.id]}
      getTitle={(items) => `${items.length} employee${items.length === 1 ? '' : 's'} selected`}
      getDescription={(items) => {
        const hceCount = items.filter(i => i.isHCE).length
        const nhceCount = items.length - hceCount
        return `${items.length} employees (${hceCount} HCE, ${nhceCount} NHCE) in selected region.`
      }}
      groupColumns={LOAN_GROUP_COLUMNS}
      color={CHART_COLORS.loan}
      height={320}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis
          type="number"
          dataKey="balance"
          name="Balance"
          tick={{ fontSize: 11, fill: TICK_FILL }}
          tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`}
          domain={[0, 'auto']}
          height={36}
        />
        <YAxis
          type="number"
          dataKey="loanBalance"
          name="Loan Balance"
          tick={{ fontSize: 11, fill: TICK_FILL }}
          tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`}
          domain={[0, 'auto']}
          width={48}
        />
        <Tooltip {...TOOLTIP_PROPS} content={<LoanScatterTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Scatter name="NHCE" data={nhceData} fill={CHART_COLORS.nhce} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
        <Scatter name="HCE" data={hceData} fill={CHART_COLORS.hce} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
      </ScatterChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function LoanDistributionChart({ data, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No loan data available" />

  return (
    <DragSelectWrapper
      chartData={data}
      chartType="bar"
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => getEmployeeIds(item)}
      getTitle={(items) => `Loan ranges: ${items.map(i => i.range).join(', ')}`}
      getDescription={(items) => `${items.reduce((s, i) => s + (i.count || 0), 0)} employees across selected loan ranges.`}
      groupColumns={LOAN_GROUP_COLUMNS}
      color={CHART_COLORS.loan}
      height={280}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis dataKey="range" tick={{ fontSize: 11, fill: TICK_FILL }} height={42} interval={0} />
        <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={42} allowDecimals={false} />
        <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip />} />
        <Bar
          dataKey="count"
          fill={CHART_COLORS.loan}
          radius={[3, 3, 0, 0]}
          name="Employees"
          className="cursor-pointer"
          {...CHART_ANIMATION_PROPS}
          onClick={(entry, _index, event) => {
            const datum = getPayload(entry)
            selectEmployeeGroup(onGroupSelect, {
              title: `Loan range ${datum.range}`,
              description: `${datum.count} employees with loans in this range.`,
              datum,
              color: CHART_COLORS.loan,
              columns: LOAN_GROUP_COLUMNS,
            }, event)
          }}
        />
      </BarChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function GenderPayGapChart({ employees, onGroupSelect }) {
  if (!employees || employees.length === 0) return <EmptyChart message="No data available" />

  const maleEmployees = employees.filter(e => e.gender === 'M')
  const femaleEmployees = employees.filter(e => e.gender === 'F')

  if (maleEmployees.length === 0 || femaleEmployees.length === 0) {
    return <EmptyChart message="Need both male and female employees for comparison" />
  }

  const avgMaleComp = maleEmployees.reduce((s, e) => s + e.compensation, 0) / maleEmployees.length
  const avgFemaleComp = femaleEmployees.reduce((s, e) => s + e.compensation, 0) / femaleEmployees.length
  const avgMaleContrib = maleEmployees.reduce((s, e) => s + e.totalEmployeeContribution, 0) / maleEmployees.length
  const avgFemaleContrib = femaleEmployees.reduce((s, e) => s + e.totalEmployeeContribution, 0) / femaleEmployees.length
  const avgMaleEmployer = maleEmployees.reduce((s, e) => s + e.employerTotal, 0) / maleEmployees.length
  const avgFemaleEmployer = femaleEmployees.reduce((s, e) => s + e.employerTotal, 0) / femaleEmployees.length
  const avgMaleDeferral = maleEmployees.reduce((s, e) => s + e.totalDeferralPercent, 0) / maleEmployees.length
  const avgFemaleDeferral = femaleEmployees.reduce((s, e) => s + e.totalDeferralPercent, 0) / femaleEmployees.length
  const maleEmployeeIds = maleEmployees.map(e => e.id)
  const femaleEmployeeIds = femaleEmployees.map(e => e.id)

  const compGap = avgFemaleComp > 0 ? ((avgMaleComp - avgFemaleComp) / avgMaleComp * 100) : 0

  const dialogColumns = [GROUP_COLUMNS.gender, GROUP_COLUMNS.employerTotal]

  const data = [
    { category: 'Avg comp', Male: avgMaleComp, Female: avgFemaleComp, maleEmployeeIds, femaleEmployeeIds },
    { category: 'Avg employee', Male: avgMaleContrib, Female: avgFemaleContrib, maleEmployeeIds, femaleEmployeeIds },
    { category: 'Avg employer', Male: avgMaleEmployer, Female: avgFemaleEmployer, maleEmployeeIds, femaleEmployeeIds },
  ]
  const selectMale = (entry, _index, event) => {
    const datum = getPayload(entry)
    selectEmployeeGroup(onGroupSelect, {
      title: `Male employees: ${datum.category}`,
      description: `${maleEmployeeIds.length} male employees represented in this metric.`,
      employeeIds: maleEmployeeIds,
      color: CHART_COLORS.male,
      columns: dialogColumns,
    }, event)
  }
  const selectFemale = (entry, _index, event) => {
    const datum = getPayload(entry)
    selectEmployeeGroup(onGroupSelect, {
      title: `Female employees: ${datum.category}`,
      description: `${femaleEmployeeIds.length} female employees represented in this metric.`,
      employeeIds: femaleEmployeeIds,
      color: CHART_COLORS.female,
      columns: dialogColumns,
    }, event)
  }

  return (
    <div className="space-y-4">
      <DragSelectWrapper
        chartData={data}
        chartType="bar"
        onGroupSelect={onGroupSelect}
        getEmployeeIds={(item) => [...(item.maleEmployeeIds || []), ...(item.femaleEmployeeIds || [])]}
        getTitle={(items) => `Gender comparison: ${items.map(i => i.category).join(', ')}`}
        getDescription={(items) => {
          const maleIds = new Set(items.flatMap(i => i.maleEmployeeIds || []))
          const femaleIds = new Set(items.flatMap(i => i.femaleEmployeeIds || []))
          return `${maleIds.size + femaleIds.size} employees (${maleIds.size} male, ${femaleIds.size} female) across selected categories.`
        }}
        groupColumns={dialogColumns}
        color={CHART_COLORS.male}
        height={280}
        chartMargin={CHART_MARGIN}
      >
        <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis dataKey="category" tick={{ fontSize: 10, fill: TICK_FILL }} height={42} interval={0} />
          <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={48} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
          <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip formatter={(value) => formatCurrency(value)} />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="Male" fill={CHART_COLORS.male} radius={[3, 3, 0, 0]} name="Male" className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={selectMale} />
          <Bar dataKey="Female" fill={CHART_COLORS.female} radius={[3, 3, 0, 0]} name="Female" className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={selectFemale} />
        </BarChart>
        </ResponsiveContainer>
      </DragSelectWrapper>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="text-muted-foreground text-xs mb-1">Compensation gap</div>
          <div className={`font-semibold ${compGap > 0 ? 'text-amber-600' : compGap < 0 ? 'text-green-600' : 'text-foreground'}`}>
            {compGap > 0 ? `${compGap.toFixed(1)}% lower for women` : compGap < 0 ? `${Math.abs(compGap).toFixed(1)}% higher for women` : 'No gap'}
          </div>
        </div>
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="text-muted-foreground text-xs mb-1">Deferral rate gap</div>
          <div className={`font-semibold ${avgMaleDeferral > avgFemaleDeferral ? 'text-amber-600' : avgMaleDeferral < avgFemaleDeferral ? 'text-green-600' : 'text-foreground'}`}>
            {avgMaleDeferral.toFixed(1)}% male vs {avgFemaleDeferral.toFixed(1)}% female
          </div>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        Based on {maleEmployees.length} male and {femaleEmployees.length} female employees
      </div>
    </div>
  )
}

function AgeDeferralHeatmap({ employees, onGroupSelect }) {
  if (!employees || employees.length === 0) return <EmptyChart message="No data available" />
  
  const ageBands = ['Under 25', '25-34', '35-44', '45-54', '55-64', '65+']
  const genders = ['M', 'F']
  
  const getAgeBand = (age) => {
    if (age === null) return null
    if (age < 25) return 'Under 25'
    if (age < 35) return '25-34'
    if (age < 45) return '35-44'
    if (age < 55) return '45-54'
    if (age < 65) return '55-64'
    return '65+'
  }
  
  const heatmapData = ageBands.map(band => {
    const row = { ageBand: band }
    genders.forEach(gender => {
      const filtered = employees.filter(e => 
        getAgeBand(e.age) === band && e.gender === gender
      )
      row[gender] = filtered.length > 0 
        ? (filtered.reduce((s, e) => s + e.totalDeferralPercent, 0) / filtered.length).toFixed(1)
        : null
      row[`${gender}EmployeeIds`] = filtered.map(e => e.id)
    })
    return row
  })
  
  const getColor = (value) => {
    if (value === null) return 'hsl(215 20% 96%)'
    const v = parseFloat(value)
    if (v === 0) return 'hsl(0 78% 94%)'
    if (v < 4) return 'hsl(32 88% 92%)'
    if (v < 8) return 'hsl(45 90% 90%)'
    if (v < 12) return 'hsl(173 56% 88%)'
    return 'hsl(217 72% 88%)'
  }

  const dialogColumns = [GROUP_COLUMNS.gender]

  const selectCell = (row, gender, event) => {
    const employeeIds = row[`${gender}EmployeeIds`]
    if (!employeeIds?.length) return
    selectEmployeeGroup(onGroupSelect, {
      title: `${row.ageBand} / ${gender === 'M' ? 'Male' : 'Female'}`,
      description: `${employeeIds.length} employees with an average deferral of ${row[gender]}%.`,
      employeeIds,
      color: gender === 'M' ? CHART_COLORS.male : CHART_COLORS.female,
      columns: dialogColumns,
    }, event)
  }

  const heatmapCells = heatmapData.flatMap((row) => genders.map((gender) => ({
    ageBand: row.ageBand,
    gender,
    averageDeferral: row[gender],
    employeeIds: row[`${gender}EmployeeIds`],
  })))
  
  return (
    <DragSelectWrapper
      chartData={heatmapCells}
      chartType="grid"
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => item.employeeIds || []}
      getTitle={(items) => `Deferral heatmap: ${items.map(item => `${item.ageBand} / ${item.gender === 'M' ? 'Male' : 'Female'}`).join(', ')}`}
      getDescription={(items) => `${new Set(items.flatMap(item => item.employeeIds || [])).size} employees across selected heatmap cells.`}
      groupColumns={dialogColumns}
      color={CHART_COLORS.primary}
      height={360}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Age band</th>
              <th className="px-3 py-2 text-center font-medium text-muted-foreground">Male</th>
              <th className="px-3 py-2 text-center font-medium text-muted-foreground">Female</th>
            </tr>
          </thead>
          <tbody>
            {heatmapData.map((row, i) => (
              <tr key={row.ageBand}>
                <td className="px-3 py-2 font-medium text-muted-foreground">{row.ageBand}</td>
                <td className="p-1 text-center">
                  <button
                    type="button"
                    data-drag-select-index={i * genders.length}
                    className="w-full px-3 py-2 text-[oklch(0.2_0.01_55)] transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ backgroundColor: getColor(row.M) }}
                    onClick={(event) => selectCell(row, 'M', event)}
                    disabled={!row.MEmployeeIds.length}
                  >
                    {row.M !== null ? `${row.M}%` : '-'}
                  </button>
                </td>
                <td className="p-1 text-center">
                  <button
                    type="button"
                    data-drag-select-index={i * genders.length + 1}
                    className="w-full px-3 py-2 text-[oklch(0.2_0.01_55)] transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ backgroundColor: getColor(row.F) }}
                    onClick={(event) => selectCell(row, 'F', event)}
                    disabled={!row.FEmployeeIds.length}
                  >
                    {row.F !== null ? `${row.F}%` : '-'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>Avg deferral:</span>
          <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(0 78% 94%)' }}>0%</span>
          <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(32 88% 92%)' }}>1–4%</span>
          <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(45 90% 90%)' }}>4–8%</span>
          <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(173 56% 88%)' }}>8–12%</span>
          <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(217 72% 88%)' }}>12%+</span>
        </div>
      </div>
    </DragSelectWrapper>
  )
}

// ==================== CENSUS DEMOGRAPHICS (no financials) ====================

function AgeDistributionChart({ data, includeInMetrics, onGroupSelect }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />

  const total = data.reduce((s, d) => s + d.count, 0)
  const employeeLabel = includeInMetrics ? 'employees' : 'active employees'

  const dialogColumns = [GROUP_COLUMNS.gender, GROUP_COLUMNS.tenure]

  return (
    <div className="space-y-2">
      <DragSelectWrapper
        chartData={data}
        chartType="bar"
        onGroupSelect={onGroupSelect}
        getEmployeeIds={(item) => getEmployeeIds(item)}
        getTitle={(items) => `Age: ${items.map(i => i.ageBand).join(', ')}`}
        getDescription={(items) => `${items.reduce((s, i) => s + (i.count || 0), 0)} ${employeeLabel} across selected age bands.`}
        groupColumns={dialogColumns}
        color={CHART_COLORS.primary}
        height={280}
        chartMargin={CHART_MARGIN}
      >
        <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis dataKey="ageBand" tick={{ fontSize: 11, fill: TICK_FILL }} height={36} interval={0} />
          <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={42} allowDecimals={false} />
          <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip />} />
          <Bar
            dataKey="count"
            fill={CHART_COLORS.primary}
            radius={[3, 3, 0, 0]}
            name="Employees"
            className="cursor-pointer"
            {...CHART_ANIMATION_PROPS}
            onClick={(entry, _index, event) => {
              const datum = getPayload(entry)
              selectEmployeeGroup(onGroupSelect, {
                title: `Age ${datum.ageBand}`,
                description: `${datum.count} ${employeeLabel} in this age band.`,
                datum,
                color: CHART_COLORS.primary,
                columns: dialogColumns,
              }, event)
            }}
          />
        </BarChart>
        </ResponsiveContainer>
      </DragSelectWrapper>
      <div className="text-xs text-muted-foreground text-center">
        Total: {total} {employeeLabel}
      </div>
    </div>
  )
}

function TenureDistributionChart({ data, includeInMetrics, onGroupSelect, genderFilter = 'all' }) {
  if (!data || data.length === 0) return <EmptyChart message="No data available" />

  const total = data.reduce((s, d) => s + d.count, 0)
  const employeeLabel = includeInMetrics ? 'employees' : 'active employees'

  // Map gender filter value to the bar dataKey for highlighting
  const activeGender = genderFilter === 'M' ? 'male' : genderFilter === 'F' ? 'female' : genderFilter === 'U' ? 'undisclosed' : null
  const dimOpacity = 0.25
  const fullOpacity = 1

  const dialogColumns = [GROUP_COLUMNS.tenure, GROUP_COLUMNS.gender]

  const handleSegmentClick = (entry, gender, event) => {
    const datum = getPayload(entry)
    const genderLabel = gender === 'male' ? 'Male' : gender === 'female' ? 'Female' : 'Undisclosed'
    const ids = datum[`${gender}Ids`] || []
    if (!ids.length) return
    selectEmployeeGroup(onGroupSelect, {
      title: `${genderLabel} – Tenure ${datum.range}`,
      description: `${ids.length} ${genderLabel.toLowerCase()} ${employeeLabel} in this tenure band.`,
      employeeIds: ids,
      color: gender === 'male' ? CHART_COLORS.male : gender === 'female' ? CHART_COLORS.female : CHART_COLORS.muted,
      columns: dialogColumns,
    }, event)
  }

  return (
    <div className="space-y-2">
      <DragSelectWrapper
        chartData={data}
        chartType="bar"
        onGroupSelect={onGroupSelect}
        getEmployeeIds={(item) => getEmployeeIds(item)}
        getTitle={(items) => `Tenure: ${items.map(i => i.range).join(', ')}`}
        getDescription={(items) => `${items.reduce((s, i) => s + (i.count || 0), 0)} ${employeeLabel} across selected tenure bands.`}
        groupColumns={dialogColumns}
        color={CHART_COLORS.secondary}
        height={290}
        chartMargin={CHART_MARGIN}
      >
        <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis dataKey="range" tick={{ fontSize: 10, fill: TICK_FILL }} angle={-18} textAnchor="end" height={54} interval={0} />
          <YAxis tick={{ fontSize: 11, fill: TICK_FILL }} width={42} allowDecimals={false} />
          <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar
            dataKey="male"
            stackId="tenure"
            fill={CHART_COLORS.male}
            fillOpacity={activeGender === null || activeGender === 'male' ? fullOpacity : dimOpacity}
            name="Male"
            className="cursor-pointer"
            {...CHART_ANIMATION_PROPS}
            onClick={(entry, _index, event) => handleSegmentClick(entry, 'male', event)}
          />
          <Bar
            dataKey="female"
            stackId="tenure"
            fill={CHART_COLORS.female}
            fillOpacity={activeGender === null || activeGender === 'female' ? fullOpacity : dimOpacity}
            name="Female"
            className="cursor-pointer"
            {...CHART_ANIMATION_PROPS}
            onClick={(entry, _index, event) => handleSegmentClick(entry, 'female', event)}
          />
          <Bar
            dataKey="undisclosed"
            stackId="tenure"
            fill={CHART_COLORS.muted}
            fillOpacity={activeGender === null || activeGender === 'undisclosed' ? fullOpacity : dimOpacity}
            radius={[3, 3, 0, 0]}
            name="Undisclosed"
            className="cursor-pointer"
            {...CHART_ANIMATION_PROPS}
            onClick={(entry, _index, event) => handleSegmentClick(entry, 'undisclosed', event)}
          />
        </BarChart>
        </ResponsiveContainer>
      </DragSelectWrapper>
      <div className="text-xs text-muted-foreground text-center">
        Total: {total} {employeeLabel}
      </div>
    </div>
  )
}

function EmptyChart({ message }) {
  return (
    <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
      {message}
    </div>
  )
}

function OwnerCapacityTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const datum = payload[0]?.payload
  if (!datum) return null
  return (
    <div className="max-w-[20rem] rounded-md border border-border bg-popover p-3 text-xs shadow-sm">
      <p className="font-semibold text-foreground">{datum.name}</p>
      <div className="mt-2 space-y-1 text-muted-foreground">
        <p className="flex justify-between gap-4"><span>Employee deferral</span><strong className="text-foreground">{formatCurrency(datum.regularDeferral)}</strong></p>
        {datum.afterTax > 0 && <p className="flex justify-between gap-4"><span>After-tax employee</span><strong className="text-foreground">{formatCurrency(datum.afterTax)}</strong></p>}
        <p className="flex justify-between gap-4"><span>Employer additions</span><strong className="text-foreground">{formatCurrency(datum.employer)}</strong></p>
        {datum.forfeitures > 0 && <p className="flex justify-between gap-4"><span>Forfeiture allocation</span><strong className="text-foreground">{formatCurrency(datum.forfeitures)}</strong></p>}
        <p className="flex justify-between gap-4 border-t border-border pt-1"><span>Total §415(c) additions</span><strong className="text-foreground">{formatCurrency(datum.annualAdditions)}</strong></p>
        <p className="flex justify-between gap-4"><span>Unused §415(c) room</span><strong className="text-foreground">{formatCurrency(datum.remaining415c)}</strong></p>
        <p className="flex justify-between gap-4"><span>Employee deferral ceiling</span><strong className="text-foreground">{formatCurrency(datum.statutoryDeferralCeiling)}</strong></p>
        {datum.catchupLimit > 0 && <p className="flex justify-between gap-4"><span>Catch-up used / limit</span><strong className="text-foreground">{formatCurrency(datum.catchupContribution)} / {formatCurrency(datum.catchupLimit)}</strong></p>}
        {datum.cashBalanceCredit > 0 && <p className="flex justify-between gap-4"><span>Cash-balance benefit credit</span><strong className="text-foreground">{formatCurrency(datum.cashBalanceCredit)}</strong></p>}
      </div>
    </div>
  )
}

function OwnerSavingsCapacityChart({ data, planYear, onGroupSelect }) {
  if (!data.length) {
    return <EmptyChart message="No owners are identified. Mark owners in Census to see this chart." />
  }
  const limits = PLAN_LIMITS[Number(planYear)] || PLAN_LIMITS[2026]
  const height = Math.max(250, data.length * 62 + 105)
  const handleClick = (entry, _index, event) => {
    const datum = getPayload(entry)
    selectEmployeeGroup(onGroupSelect, {
      title: `${datum.name} — owner savings`,
      description: `${formatCurrency(datum.annualAdditions)} of ${formatCurrency(datum.limit415c)} in modeled §415(c) annual additions.`,
      datum,
      color: CHART_COLORS.primary,
      columns: [GROUP_COLUMNS.actualPretax, GROUP_COLUMNS.actualRoth, GROUP_COLUMNS.afterTax, GROUP_COLUMNS.employerTotal, GROUP_COLUMNS.cashBalance],
    }, event)
  }

  return (
    <div className="space-y-2">
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: TICK_FILL }}
              tickFormatter={(value) => `$${Math.round(value / 1000)}K`}
              domain={[0, 'dataMax']}
            />
            <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: 11, fill: TICK_FILL }} />
            <Tooltip {...TOOLTIP_PROPS} content={<OwnerCapacityTooltip />} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="regularDeferral" stackId="additions" name="Employee deferral" fill={CHART_COLORS.preTax} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
            <Bar dataKey="afterTax" stackId="additions" name="After-tax employee" fill={CHART_COLORS.afterTax} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
            <Bar dataKey="employer" stackId="additions" name="Employer additions" fill={CHART_COLORS.employer} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
            <Bar dataKey="forfeitures" stackId="additions" name="Forfeitures" fill={CHART_COLORS.success} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
            <Bar dataKey="otherAdditions" stackId="additions" name="Other additions" fill={CHART_COLORS.secondary} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
            <Bar dataKey="remaining415c" stackId="additions" name="Unused §415(c) room" fill={CHART_COLORS.inactive} radius={[0, 3, 3, 0]} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        {limits.year} §415(c) annual-additions room uses each owner’s applicable compensation limit. Catch-up contributions are outside §415(c). The employee-deferral ceiling is {formatCurrency(limits.deferralLimit)}, plus the applicable age-based catch-up, but plan terms and nondiscrimination rules may require a lower amount.
      </p>
      {data.some((row) => row.cashBalanceCredit > 0) && (
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          Cash-balance benefit credits are shown in the tooltip only. They are outside §415(c), are not a guaranteed maximum or required contribution, and require actuarial funding and §415(b) review.
        </p>
      )}
    </div>
  )
}

function OwnerPlanTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const datum = payload[0]?.payload
  if (!datum) return null
  return (
    <div className="max-w-[20rem] rounded-md border border-border bg-popover p-3 text-xs shadow-sm">
      <p className="font-semibold text-foreground">{datum.name}</p>
      <div className="mt-2 space-y-1 text-muted-foreground">
        <p className="flex justify-between gap-4"><span>Owner employee savings</span><strong className="text-foreground">{formatCurrency(datum.ownerEmployeeSavings)}</strong></p>
        <p className="flex justify-between gap-4"><span>Owner DC employer</span><strong className="text-foreground">{formatCurrency(datum.ownerDcEmployer)}</strong></p>
        {datum.ownerCashBalance > 0 && <p className="flex justify-between gap-4"><span>Owner cash-balance credit</span><strong className="text-foreground">{formatCurrency(datum.ownerCashBalance)}</strong></p>}
        <p className="flex justify-between gap-4 border-t border-border pt-1"><span>Owner total allocation</span><strong className="text-foreground">{formatCurrency(datum.ownerAllocation)}</strong></p>
        <p className="flex justify-between gap-4"><span>Non-owner DC employer</span><strong className="text-foreground">{formatCurrency(datum.nonOwnerDcEmployer)}</strong></p>
        {datum.nonOwnerCashBalance > 0 && <p className="flex justify-between gap-4"><span>Non-owner cash-balance credit</span><strong className="text-foreground">{formatCurrency(datum.nonOwnerCashBalance)}</strong></p>}
      </div>
    </div>
  )
}

function OwnerPlanComparisonChart({ data, activePlanKey }) {
  if (!data.length) {
    return <EmptyChart message="No owners are identified. Mark owners in Census to compare plan designs." />
  }
  const plotted = data.map((point) => ({
    ...point,
    fill: point.key === activePlanKey ? CHART_COLORS.primary : CHART_COLORS.warning,
  }))
  return (
    <div className="space-y-2">
      <div className="h-[310px]">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 14, right: 20, left: 12, bottom: 36 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis
              type="number"
              dataKey="nonOwnerAllocation"
              name="Non-owner modeled allocation"
              tick={{ fontSize: 10, fill: TICK_FILL }}
              tickFormatter={(value) => `$${Math.round(value / 1000)}K`}
              domain={[0, 'auto']}
              label={{ value: 'Non-owner modeled employer allocation', position: 'insideBottom', offset: -20, fontSize: 10, fill: TICK_FILL }}
            />
            <YAxis
              type="number"
              dataKey="ownerAllocation"
              name="Owner retirement allocation"
              tick={{ fontSize: 10, fill: TICK_FILL }}
              tickFormatter={(value) => `$${Math.round(value / 1000)}K`}
              domain={[0, 'auto']}
              width={54}
            />
            <Tooltip {...TOOLTIP_PROPS} content={<OwnerPlanTooltip />} />
            <Scatter name="Calculated plan designs" data={plotted} {...CHART_ANIMATION_PROPS}>
              {plotted.map((point) => <Cell key={point.key} fill={point.fill} />)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {plotted.map((point) => (
          <span key={point.key} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: point.fill }} />
            {point.name}{point.key === activePlanKey ? ' (current)' : ''}
          </span>
        ))}
      </div>
      {data.length === 1 && <p className="text-[11px] text-muted-foreground">Add another plan design to compare the owner/non-owner tradeoff.</p>}
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Planning comparison only—not a formal compliance determination. Cash-balance values are modeled benefit credits, not contribution requirements; funding must be determined by the plan actuary. Review the Checks tab before presenting a design.
      </p>
    </div>
  )
}

function NoDataMessage() {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-foreground mb-1">No data to visualize</h3>
      <p className="text-sm text-muted-foreground text-center max-w-sm">
        Add employee data in the Inputs pane to see visualizations here.
      </p>
    </div>
  )
}

function OverviewInfoBoxes({ employees, onGroupSelect }) {
  const [lowSaverThreshold, setLowSaverThreshold] = useState(3)
  const [nearRetireAge, setNearRetireAge] = useState(50)
  const [editingField, setEditingField] = useState(null) // 'lowSaver' | 'nearRetire' | null
  const [editValue, setEditValue] = useState('')

  const hces = employees.filter(e => e.isHCE)
  const nhces = employees.filter(e => !e.isHCE)
  const hceAvgDeferral = hces.length > 0
    ? hces.reduce((sum, e) => sum + e.totalDeferralPercent, 0) / hces.length
    : 0
  const nhceAvgDeferral = nhces.length > 0
    ? nhces.reduce((sum, e) => sum + e.totalDeferralPercent, 0) / nhces.length
    : 0
  const adpGap = hceAvgDeferral - nhceAvgDeferral
  const nonParticipants = employees.filter(e => e.participationStatus === 'non-participant')
  const lowSavers = employees.filter(e => e.totalDeferralPercent > 0 && e.totalDeferralPercent < lowSaverThreshold)
  const nearRetirees = employees.filter(e => e.age >= nearRetireAge)

  const handleBoxClick = (title, description, employeeIds, color, event) => {
    if (!employeeIds.length) return
    onGroupSelect?.({ title, description, employeeIds, color, additive: Boolean(event?.shiftKey) })
  }

  const startEditing = (field, currentValue) => {
    setEditingField(field)
    setEditValue(String(currentValue))
  }

  const commitEdit = () => {
    const parsed = parseFloat(editValue)
    if (!isNaN(parsed) && parsed > 0) {
      if (editingField === 'lowSaver') {
        setLowSaverThreshold(parsed)
      } else if (editingField === 'nearRetire') {
        setNearRetireAge(Math.round(parsed))
      }
    }
    setEditingField(null)
    setEditValue('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit()
    if (e.key === 'Escape') {
      setEditingField(null)
      setEditValue('')
    }
  }

  const boxes = [
    {
      label: 'HCE Avg Deferral',
      value: formatPercent(hceAvgDeferral),
      color: CHART_COLORS.hce,
      employeeIds: hces.map(e => e.id),
      description: `${hces.length} HCE employees with avg deferral ${formatPercent(hceAvgDeferral)}.`,
    },
    {
      label: 'NHCE Avg Deferral',
      value: formatPercent(nhceAvgDeferral),
      color: CHART_COLORS.nhce,
      employeeIds: nhces.map(e => e.id),
      description: `${nhces.length} NHCE employees with avg deferral ${formatPercent(nhceAvgDeferral)}.`,
    },
    {
      label: 'Simplified HCE/NHCE gap',
      value: `${adpGap >= 0 ? '+' : ''}${adpGap.toFixed(2)}%`,
      color: adpGap > 2 ? CHART_COLORS.warning : CHART_COLORS.primary,
      employeeIds: [...hces.map(e => e.id), ...nhces.map(e => e.id)],
      description: `Simplified gap of ${adpGap.toFixed(2)}% between HCE and NHCE average deferrals; this is not a formal ADP test.`,
    },
    {
      label: 'Non-participants',
      value: nonParticipants.length,
      color: CHART_COLORS.inactive,
      employeeIds: nonParticipants.map(e => e.id),
      description: `${nonParticipants.length} employees not participating in the plan.`,
    },
    {
      id: 'lowSaver',
      displayLabel: 'Low Savers',
      label: `Low Savers (<${lowSaverThreshold}%)`,
      value: lowSavers.length,
      color: CHART_COLORS.warning,
      employeeIds: lowSavers.map(e => e.id),
      description: `${lowSavers.length} employees deferring between 0% and ${lowSaverThreshold}%.`,
      threshold: lowSaverThreshold,
      thresholdSuffix: '%',
    },
    {
      id: 'nearRetire',
      displayLabel: 'Near-Retire',
      label: `Near-Retire (${nearRetireAge}+)`,
      value: nearRetirees.length,
      color: CHART_COLORS.danger,
      employeeIds: nearRetirees.map(e => e.id),
      description: `${nearRetirees.length} employees aged ${nearRetireAge} or older.`,
      threshold: nearRetireAge,
      thresholdSuffix: '',
    },
  ]

  return (
    <div className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(13rem,1fr))]">
      {boxes.map((box) => (
        <div
          key={box.id || box.label}
          className="flex min-h-[7.25rem] flex-col rounded border border-border bg-muted/25 p-3"
        >
          <span className="text-[11px] uppercase leading-tight tracking-wide text-muted-foreground">
            {box.displayLabel || box.label}
          </span>

          {box.id && (
            <div className="mt-1.5">
              {editingField === box.id ? (
                <label className="flex min-h-9 items-center gap-2 rounded border border-primary bg-background px-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    {box.id === 'lowSaver' ? 'Cutoff under' : 'Age cutoff'}
                  </span>
                  <input
                    type="number"
                    min="1"
                    step={box.id === 'lowSaver' ? '0.5' : '1'}
                    className="h-7 min-w-0 flex-1 border-b border-primary bg-transparent text-center font-mono text-base font-semibold text-foreground outline-none"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={handleKeyDown}
                    aria-label={box.id === 'lowSaver' ? 'Low saver percentage cutoff' : 'Near-retirement age cutoff'}
                    autoFocus
                  />
                  <span className="text-sm font-semibold text-foreground">
                    {box.id === 'lowSaver' ? '%' : '+'}
                  </span>
                </label>
              ) : (
                <button
                  type="button"
                  className="flex min-h-9 w-full items-center justify-between gap-2 rounded border border-border bg-background px-2 text-left text-xs font-medium text-foreground transition-colors hover:border-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => startEditing(box.id, box.threshold)}
                  aria-label={`Edit ${box.displayLabel} cutoff, currently ${box.threshold}${box.thresholdSuffix}${box.id === 'nearRetire' ? ' and older' : ''}`}
                >
                  <span>
                    {box.id === 'lowSaver' ? `Under ${box.threshold}%` : `Age ${box.threshold}+`}
                  </span>
                  <span className="flex items-center gap-1 text-primary">
                    <Pencil className="h-3.5 w-3.5" />
                    Edit cutoff
                  </span>
                </button>
              )}
            </div>
          )}

          <button
            type="button"
            className="group mt-auto flex w-full items-end justify-between gap-2 rounded py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default"
            onClick={(event) => handleBoxClick(box.label, box.description, box.employeeIds, box.color, event)}
            disabled={!box.employeeIds.length}
            aria-label={box.employeeIds.length
              ? `View ${box.employeeIds.length} employees for ${box.label}`
              : `No employees to view for ${box.label}`}
          >
            <span className="font-mono text-xl font-semibold text-foreground">{box.value}</span>
            {box.employeeIds.length > 0 ? (
              <span className="flex items-center gap-1 text-xs font-medium text-primary group-hover:underline">
                View employees
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">No employees</span>
            )}
          </button>
        </div>
      ))}
    </div>
  )
}

export { OverviewInfoBoxes, LoanByAgeBandChart, LoanVsBalanceScatter, LoanDistributionChart }

// Shared chart primitives reused by Julio's generic chart card and the CFO
// summary's charts so both get the exact same shell, group drill-in,
// reveal-identifiers dialog, Shift-combine bar, colors, and click payload
// handling as the built-in visualizations.
export { ChartCard, EmployeeGroupDialog, selectEmployeeGroup, getPayload, EmptyChart, COLORS, CHART_MARGIN }
export { useChartGroupSelection, MultiSelectionBar, GROUP_COLUMNS }

// Forfeiture lens: tie this year's terminations to the vesting schedule.
// Leavers who aren't fully vested forfeit the unvested employer money back to
// the plan, which can offset future employer cost – a key vesting-design lever.
function computeForfeiture(employees) {
    if (!employees || employees.length === 0) return null
    const leavers = employees.filter(e => e.isTerminated)
    const totalEmployer = employees.reduce((s, e) => s + (e.employerTotal || 0), 0)
    const leaverEmployer = leavers.reduce((s, e) => s + (e.employerTotal || 0), 0)
    const vestedOut = leavers.reduce((s, e) => s + (e.vestedEmployerTotal || 0), 0)
    const forfeited = leavers.reduce((s, e) => s + (e.estimatedForfeiture || 0), 0)
    const unresolved = leavers.reduce((s, e) => s + (e.forfeitureUnresolvedAmount || 0), 0)
    const unresolvedCount = leavers.filter(e => (e.forfeitureUnresolvedAmount || 0) > 0).length
    const vestingActive = employees.some(e => e.vestingEnabled)
    const withEmployer = leavers.filter(e => (e.employerTotal || 0) > 0)
    const resolvedWithEmployer = withEmployer.filter(e => !(e.vestingUnresolvedAmount > 0))
    const bucketEmployees = {
      full: [],
      partial: [],
      none: [],
    }
    for (const e of resolvedWithEmployer) {
      const pct = e.vestedEmployerTotal / e.employerTotal
      if (pct >= 0.999) bucketEmployees.full.push(e.id)
      else if (pct <= 0.001) bucketEmployees.none.push(e.id)
      else bucketEmployees.partial.push(e.id)
    }
    return {
      leaverCount: leavers.length,
      leaverWithEmployer: withEmployer.length,
      totalEmployer,
      leaverEmployer,
      vestedOut,
      forfeited,
      unresolved,
      unresolvedCount,
      allRelevantUnresolved: withEmployer.length > 0
        && resolvedWithEmployer.length === 0
        && forfeited === 0
        && vestedOut === 0,
      vestingActive,
      avgVestedPct: leaverEmployer > 0 ? (vestedOut / leaverEmployer) * 100 : null,
      offsetPct: totalEmployer > 0 ? (forfeited / totalEmployer) * 100 : 0,
      buckets: [
        { name: 'Fully vested', value: bucketEmployees.full.length, fill: CHART_COLORS.success, employeeIds: bucketEmployees.full },
        { name: 'Partially vested', value: bucketEmployees.partial.length, fill: CHART_COLORS.warning, employeeIds: bucketEmployees.partial },
        { name: 'Unvested', value: bucketEmployees.none.length, fill: CHART_COLORS.danger, employeeIds: bucketEmployees.none },
      ],
    }
}

function TurnoverSection({ turnoverA, employeesA, onGroupSelect }) {
  const [granularity, setGranularity] = useState('quarterly')
  const [showShortTermIds, setShowShortTermIds] = useState(false)

  const forfeitureA = useMemo(() => computeForfeiture(employeesA), [employeesA])

  if (!turnoverA) return null

  return (
    <ChartSection id="visualizations-turnover" title="Turnover">
      <ComparableCard
        id="chart-headcount-movement"
        title="Headcount movement"
        description="Hires and terminations by period, with annual summary"
        dataA={turnoverA}
      >
        {(turnover) => {
          const periods = turnover[granularity] || []
          const hasAnyData = periods.some(p => p.starting > 0 || p.hires > 0 || p.terminations > 0)
          const annual = turnover.annual?.[0]
          return (
            <>
              <div className="mb-3 flex items-center gap-2">
                <Select value={granularity} onValueChange={setGranularity}>
                  <SelectTrigger className="w-32 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semiannual">Semiannual</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
                {turnover.dateIssueCount > 0 && (
                  <span className="text-xs text-amber-600">{turnover.dateIssueCount} employee status/date issue{turnover.dateIssueCount === 1 ? '' : 's'}; unusable Terminated dates are excluded and Active/Leave termination dates are ignored</span>
                )}
              </div>
              {!hasAnyData ? (
                <EmptyChart message="No hire or termination date data available for turnover reporting." />
              ) : (
                <>
                  <DragSelectWrapper
                    chartData={periods}
                    chartType="bar"
                    onGroupSelect={onGroupSelect}
                    getEmployeeIds={(item) => item.employeeIds || []}
                    getTitle={(items) => `Turnover: ${items.map(item => item.label).join(', ')}`}
                    getDescription={(items) => `${new Set(items.flatMap(item => item.employeeIds || [])).size} employees hired or terminated across selected periods.`}
                    groupColumns={[GROUP_COLUMNS.hireDate, GROUP_COLUMNS.terminationDate, GROUP_COLUMNS.tenure]}
                    color={CHART_COLORS.primary}
                    height={220}
                    chartMargin={CHART_MARGIN}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={periods} margin={CHART_MARGIN}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                        <XAxis dataKey="label" tick={{ fill: TICK_FILL, fontSize: 11 }} />
                        <YAxis tick={{ fill: TICK_FILL, fontSize: 11 }} allowDecimals={false} />
                        <Tooltip
                          {...TOOLTIP_PROPS}
                          formatter={(value, name) => [value, name]}
                          labelFormatter={(label) => `Period: ${label}`}
                        />
                        <Legend />
                        <Bar
                          dataKey="hires"
                          name="Hires"
                          fill={CHART_COLORS.success}
                          className="cursor-pointer"
                          {...CHART_ANIMATION_PROPS}
                          onClick={(entry, _index, event) => {
                            const datum = getPayload(entry)
                            selectEmployeeGroup(onGroupSelect, {
                              title: `Hires: ${datum.label}`,
                              description: `${datum.hireEmployeeIds?.length || 0} employees hired in this period.`,
                              employeeIds: datum.hireEmployeeIds || [],
                              color: CHART_COLORS.success,
                              columns: [GROUP_COLUMNS.hireDate, GROUP_COLUMNS.tenure],
                            }, event)
                          }}
                        />
                        <Bar
                          dataKey="terminations"
                          name="Terminations"
                          fill={CHART_COLORS.danger}
                          className="cursor-pointer"
                          {...CHART_ANIMATION_PROPS}
                          onClick={(entry, _index, event) => {
                            const datum = getPayload(entry)
                            selectEmployeeGroup(onGroupSelect, {
                              title: `Terminations: ${datum.label}`,
                              description: `${datum.terminationEmployeeIds?.length || 0} employees terminated in this period.`,
                              employeeIds: datum.terminationEmployeeIds || [],
                              color: CHART_COLORS.danger,
                              columns: [GROUP_COLUMNS.terminationDate, GROUP_COLUMNS.tenure],
                            }, event)
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </DragSelectWrapper>
                  {annual && (
                    <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
                      <div className="rounded border p-2">
                        <div className="font-mono font-semibold text-sm">{annual.starting}</div>
                        <div className="text-muted-foreground">Starting</div>
                      </div>
                      <div className="rounded border p-2">
                        <div className="font-mono font-semibold text-sm text-emerald-700">{annual.hires}</div>
                        <div className="text-muted-foreground">Hires</div>
                      </div>
                      <div className="rounded border p-2">
                        <div className="font-mono font-semibold text-sm text-red-700">{annual.terminations}</div>
                        <div className="text-muted-foreground">Terminations</div>
                      </div>
                      <div className="rounded border p-2">
                        <div className="font-mono font-semibold text-sm">{annual.turnoverRate}%</div>
                        <div className="text-muted-foreground">Turnover rate</div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )
        }}
      </ComparableCard>

      {forfeitureA?.leaverCount > 0 && (
        <ComparableCard
          id="chart-forfeitures"
          title="Forfeitures from turnover"
          description="How leavers' vesting status converts turnover into recaptured employer cost"
          dataA={forfeitureA}
        >
          {(forfeiture) => !forfeiture || forfeiture.leaverCount === 0 ? (
            <p className="text-sm text-muted-foreground">No terminations in this plan, so there is nothing to forfeit.</p>
          ) : !forfeiture.vestingActive ? (
            <p className="text-sm text-muted-foreground">
              Vesting is set to immediate (100%), so terminations forfeit nothing –
              all employer contributions stay with the employee. Add a vesting
              schedule in Plan settings to see how turnover recaptures employer cost.
            </p>
          ) : forfeiture.allRelevantUnresolved ? (
            <p className="text-sm text-muted-foreground">
              Forfeiture values are unavailable because every terminated employee with employer contributions has unresolved service-dependent vesting. {formatCurrency(forfeiture.unresolved)} remains unresolved.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 text-center text-xs">
                <div className="rounded border p-2">
                  <div className="font-mono text-base font-semibold text-emerald-700">{formatCurrency(forfeiture.forfeited)}</div>
                  <div className="text-muted-foreground">Forfeited back to plan</div>
                </div>
                <div className="rounded border p-2">
                  <div className="font-mono text-base font-semibold">{formatPercent(forfeiture.offsetPct)}</div>
                  <div className="text-muted-foreground">Offset of total employer cost</div>
                </div>
                <div className="rounded border p-2">
                  <div className="font-mono text-base font-semibold">{formatCurrency(forfeiture.vestedOut)}</div>
                  <div className="text-muted-foreground">Vested $ retained by leavers</div>
                </div>
                <div className="rounded border p-2">
                  <div className="font-mono text-base font-semibold">{forfeiture.avgVestedPct == null ? '–' : formatPercent(forfeiture.avgVestedPct)}</div>
                  <div className="text-muted-foreground">Avg vested % of leavers</div>
                </div>
                {forfeiture.unresolved > 0 && (
                  <div className="col-span-2 rounded border border-amber-200 bg-amber-50 p-2">
                    <div className="font-mono text-base font-semibold text-amber-700">{formatCurrency(forfeiture.unresolved)}</div>
                    <div className="text-amber-700">{forfeiture.unresolvedCount} terminated employee{forfeiture.unresolvedCount === 1 ? '' : 's'} with unresolved vesting</div>
                  </div>
                )}
              </div>
              {forfeiture.buckets.some(bucket => bucket.value > 0) && (
                <>
                  <div className="mt-3 mb-1 text-xs font-medium text-muted-foreground">Leavers by vested status</div>
                  <DragSelectWrapper
                    chartData={forfeiture.buckets}
                    chartType="bar"
                    onGroupSelect={onGroupSelect}
                    getEmployeeIds={(item) => item.employeeIds || []}
                    getTitle={(items) => `Leaver vesting: ${items.map(item => item.name).join(', ')}`}
                    getDescription={(items) => `${new Set(items.flatMap(item => item.employeeIds || [])).size} terminated employees across selected vesting categories.`}
                    groupColumns={[GROUP_COLUMNS.terminationDate, GROUP_COLUMNS.vestedEmployer, GROUP_COLUMNS.forfeiture]}
                    color={CHART_COLORS.warning}
                    height={150}
                    chartMargin={{ top: 4, right: 18, left: 4, bottom: 4 }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={forfeiture.buckets} layout="vertical" margin={{ top: 4, right: 18, left: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                        <XAxis type="number" tick={{ fill: TICK_FILL, fontSize: 11 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" width={96} tick={{ fill: TICK_FILL, fontSize: 11 }} />
                        <Tooltip {...TOOLTIP_PROPS} formatter={(value) => [value, 'Leavers']} />
                        <Bar
                          dataKey="value"
                          name="Leavers"
                          className="cursor-pointer"
                          {...CHART_ANIMATION_PROPS}
                          onClick={(entry, _index, event) => {
                            const datum = getPayload(entry)
                            selectEmployeeGroup(onGroupSelect, {
                              title: datum.name,
                              description: `${datum.value} terminated employees in this vesting category.`,
                              datum,
                              color: datum.fill,
                              columns: [GROUP_COLUMNS.terminationDate, GROUP_COLUMNS.vestedEmployer, GROUP_COLUMNS.forfeiture],
                            }, event)
                          }}
                        >
                          {forfeiture.buckets.map((b) => (
                            <Cell key={b.name} fill={b.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </DragSelectWrapper>
                </>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Known values only. Amounts with unresolved service-dependent vesting
                are excluded from forfeiture totals and shown separately.
              </p>
              </>
            )
          }
        </ComparableCard>
      )}

      {turnoverA.shortTermTerminations?.length > 0 && (
        <ComparableCard
          id="chart-short-term-terminations"
          title={`Short-term terminations (≤ ${turnoverA.shortTermThresholdDays} days)`}
          description="Leavers who left within the threshold period of their hire date"
          dataA={turnoverA.shortTermTerminations}
        >
          {(shortTermTerminations) => (
            <>
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="text-2xl font-semibold">{shortTermTerminations.length}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0"
                  onClick={() => setShowShortTermIds((value) => !value)}
                >
                  {showShortTermIds ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  {showShortTermIds ? 'Hide Employee Details' : 'Show Employee Details'}
                </Button>
              </div>
              <div className="max-h-48 overflow-auto rounded border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/80">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">{showShortTermIds ? 'ID' : 'Employee'}</th>
                      {showShortTermIds && <th className="px-3 py-1.5 text-left font-medium">Name</th>}
                      {showShortTermIds && <th className="px-3 py-1.5 text-right font-medium">Age</th>}
                      <th className="px-3 py-1.5 text-right font-medium">Tenure (days)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shortTermTerminations.map((e, index) => (
                      <tr key={e.id} className="border-t border-border">
                        <td className="px-3 py-1 font-mono">{showShortTermIds ? e.id : `Employee ${index + 1}`}</td>
                        {showShortTermIds && <td className="px-3 py-1">{e.name || '-'}</td>}
                        {showShortTermIds && <td className="px-3 py-1 text-right font-mono">{e.age ?? '-'}</td>}
                        <td className="px-3 py-1 text-right font-mono">{e.tenureDays ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </ComparableCard>
      )}
    </ChartSection>
  )
}

function MultiSelectionBar({ selection, onView, onClear }) {
  if (!selection?.employeeIds?.length) return null

  return (
    <div className="fixed inset-x-3 bottom-24 z-40 mx-auto flex max-w-xl items-center gap-3 border border-primary/30 bg-background/95 p-3 shadow-lg backdrop-blur sm:bottom-5">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Users className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{selection.employeeIds.length} employees selected</p>
        <p className="truncate text-xs text-muted-foreground">
          {selection.sourceCount} chart selection{selection.sourceCount === 1 ? '' : 's'} · Shift-click or Shift-drag to add more
        </p>
      </div>
      <Button type="button" size="sm" onClick={onView}>View</Button>
      <button
        type="button"
        className="flex h-8 w-8 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
        onClick={onClear}
        aria-label="Clear chart selection"
        title="Clear selection"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

// ==================== CONFIGURED (JULIO) CHARTS ====================
// One generic renderer for a chart built from a saved/proposed config. It reuses
// the same ChartCard shell, DragSelectWrapper (rectangle + lasso + shift-click
// additive select), group click-through, and EmployeeGroupDialog (reveal
// identifiers) as every built-in chart – so a Julio chart behaves identically.

function ConfiguredBarBody({ built, onGroupSelect }) {
  const { data, render } = built
  const dialogColumns = configuredGroupColumns(render)
  return (
    <DragSelectWrapper
      chartType="bar"
      chartData={data}
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => item.employeeIds || []}
      getTitle={(items) => items.map((i) => i.label).join(', ')}
      getDescription={(items) => `${items.reduce((sum, i) => sum + (i.count || 0), 0)} employees across selected groups.`}
      groupColumns={dialogColumns}
      color={CHART_COLORS.primary}
      height={280}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis dataKey={render.xKey} tick={{ fontSize: 10, fill: TICK_FILL }} height={48} interval={0} angle={-15} textAnchor="end" />
          <YAxis
            tick={{ fontSize: 11, fill: TICK_FILL }}
            width={48}
            allowDecimals={false}
            tickFormatter={(v) => (render.aggregate && render.aggregate !== 'count' && render.metric ? formatFieldValue(render.metric, v) : `${v}`)}
          />
          <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip />} />
          <Bar
            dataKey={render.valueKey}
            fill={CHART_COLORS.primary}
            radius={[3, 3, 0, 0]}
            name={render.valueKey === 'count' ? 'Employees' : (render.metric || 'Value')}
            className="cursor-pointer"
            {...CHART_ANIMATION_PROPS}
            onClick={(entry, _index, event) => {
              const datum = getPayload(entry)
              selectEmployeeGroup(onGroupSelect, {
                title: datum.label,
                description: `${datum.count} employees in this group.`,
                datum,
                color: CHART_COLORS.primary,
                columns: dialogColumns,
              }, event)
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function ConfiguredPieBody({ built, onGroupSelect }) {
  const { data, render } = built
  const total = data.reduce((sum, item) => sum + (item.value || 0), 0)
  const colorFor = (index) => COLORS[index % COLORS.length]
  const dialogColumns = configuredGroupColumns(render)
  const handleSelect = (datum, color, event) => {
    selectEmployeeGroup(onGroupSelect, {
      title: datum.name,
      description: `${datum.count} employees in ${datum.name}.`,
      datum,
      color,
      columns: dialogColumns,
    }, event)
  }
  return (
    <div className="space-y-3">
      <DragSelectWrapper
        chartType="pie"
        chartData={data}
        onGroupSelect={onGroupSelect}
        getEmployeeIds={(item) => item.employeeIds || []}
        getTitle={(items) => items.map((i) => i.name).join(', ')}
        getDescription={(items) => `${new Set(items.flatMap((i) => i.employeeIds || [])).size} employees across selected slices.`}
        groupColumns={dialogColumns}
        color={CHART_COLORS.primary}
        height={240}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="52%"
              outerRadius="78%"
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
              className="cursor-pointer"
              {...CHART_ANIMATION_PROPS}
              onClick={(entry, index, event) => handleSelect(getPayload(entry), colorFor(index), event)}
            >
              {data.map((entry, index) => (
                <Cell key={entry.name} fill={colorFor(index)} />
              ))}
            </Pie>
            <Tooltip {...TOOLTIP_PROPS} content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </DragSelectWrapper>
      <div className="grid gap-2 sm:grid-cols-3">
        {data.map((item, index) => {
          const pct = total > 0 ? (item.value / total) * 100 : 0
          return (
            <button
              key={item.name}
              type="button"
              className="flex min-w-0 items-center gap-2 border border-border bg-muted/25 px-2 py-2 text-left text-xs transition-colors hover:bg-muted/50"
              onClick={(event) => handleSelect(item, colorFor(index), event)}
            >
              <span className="h-2.5 w-2.5 shrink-0" style={{ backgroundColor: colorFor(index) }} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-foreground">{item.name}</span>
                <span className="font-mono text-[11px] text-muted-foreground">{item.count} / {pct.toFixed(1)}%</span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ConfiguredScatterBody({ built, onGroupSelect }) {
  const { data, scatterDataSets, render } = built
  const seriesColor = (label, index) => {
    if (render.seriesField === 'isHCE') return label === 'HCE' ? CHART_COLORS.hce : CHART_COLORS.nhce
    return COLORS[index % COLORS.length]
  }
  const dialogColumns = configuredGroupColumns(render)
  const handleClick = (entry, _index, event) => {
    const datum = getPayload(entry)
    selectEmployeeGroup(onGroupSelect, {
      title: 'Employee point',
      description: `${render.xLabel}: ${formatFieldValue(render.xKey, datum[render.xKey])}, ${render.yLabel}: ${formatFieldValue(render.yKey, datum[render.yKey])}.`,
      datum,
      employeeIds: [datum.id],
      color: CHART_COLORS.primary,
      columns: dialogColumns,
    }, event)
  }
  return (
    <DragSelectWrapper
      chartType="scatter"
      chartData={data}
      scatterDataSets={scatterDataSets}
      onGroupSelect={onGroupSelect}
      getEmployeeIds={(item) => [item.id]}
      getTitle={(items) => `${items.length} employee${items.length === 1 ? '' : 's'} selected`}
      getDescription={(items) => `${items.length} employees in selected region.`}
      groupColumns={dialogColumns}
      color={CHART_COLORS.primary}
      height={320}
      chartMargin={CHART_MARGIN}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis type="number" dataKey={render.xKey} name={render.xLabel} tick={{ fontSize: 11, fill: TICK_FILL }} tickFormatter={(v) => formatFieldValue(render.xKey, v)} domain={['auto', 'auto']} height={36} />
          <YAxis type="number" dataKey={render.yKey} name={render.yLabel} tick={{ fontSize: 11, fill: TICK_FILL }} tickFormatter={(v) => formatFieldValue(render.yKey, v)} domain={['auto', 'auto']} width={46} />
          <Tooltip {...TOOLTIP_PROPS} content={<ScatterTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {render.seriesValues.map((label, index) => (
            <Scatter key={label} name={label} data={scatterDataSets[index]} fill={seriesColor(label, index)} className="cursor-pointer" {...CHART_ANIMATION_PROPS} onClick={handleClick} />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    </DragSelectWrapper>
  )
}

function safeBuildChart(config, employees) {
  try {
    return { chart: buildChartData(config, employees || []), error: null }
  } catch (error) {
    return { chart: null, error: error.message }
  }
}

function configuredChartBody(built, onGroupSelect) {
  if (built.error) return <EmptyChart message={built.error} />
  if (built.chart.chartType === 'bar') return <ConfiguredBarBody built={built.chart} onGroupSelect={onGroupSelect} />
  if (built.chart.chartType === 'pie') return <ConfiguredPieBody built={built.chart} onGroupSelect={onGroupSelect} />
  return <ConfiguredScatterBody built={built.chart} onGroupSelect={onGroupSelect} />
}

// Render one configured chart from the store's calculated employees (so it
// stays live). While comparing it follows the shared plan chips like every
// other chart; otherwise it shows the plan it was saved against, falling back
// to Plan A when that plan no longer exists. Same drill-in/reveal dialog as
// every chart.
export function ConfiguredChart({ config, planKey, title, headerAction = null }) {
  const comparing = useStore(isComparing)
  const activePlanKey = useStore(resolveActivePlanKey)
  const shownKey = comparing ? activePlanKey : (PLAN_SLOTS.includes(planKey) ? planKey : 'planA')
  const shownEmployees = useStore((state) => state.analysis?.[shownKey]?.calculatedEmployees)
  const baselineEmployees = useStore((state) => state.analysis?.planA?.calculatedEmployees)
  const activeEmployees = shownEmployees?.length ? shownEmployees : baselineEmployees
  const { selectedGroup, pendingSelection, handleGroupSelect, viewPendingSelection, closeGroup, clearPending } = useChartGroupSelection()

  const active = useMemo(() => safeBuildChart(config, activeEmployees), [config, activeEmployees])

  const note = active.chart && active.chart.meta?.suppressedCount > 0
    ? `${active.chart.meta.suppressedCount} employees in groups too small to show are omitted to protect privacy.`
    : null

  const combinedHeaderAction = headerAction ? (
    <div className="flex items-center gap-2">
      {headerAction}
    </div>
  ) : null

  return (
    <ChartCard title={title} note={note} headerAction={combinedHeaderAction}>
      {configuredChartBody(active, handleGroupSelect)}
      <EmployeeGroupDialog
        group={selectedGroup}
        employees={activeEmployees || []}
        onClose={closeGroup}
      />
      <MultiSelectionBar
        selection={pendingSelection}
        onView={viewPendingSelection}
        onClear={clearPending}
      />
    </ChartCard>
  )
}

function CustomChartsSection() {
  const customCharts = useStore((state) => state.customCharts)
  const removeCustomChart = useStore((state) => state.removeCustomChart)

  return (
    <ChartSection id="visualizations-custom" title="Custom charts">
      {customCharts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
              <BarChart3 className="h-5 w-5" />
            </span>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">No custom charts yet</p>
              <p className="mx-auto max-w-sm text-xs text-muted-foreground">
                Ask Julio to chart your census – &ldquo;show participation by age&rdquo;, &ldquo;compensation vs employer cost&rdquo; – then click <span className="font-medium">Add to my charts</span>. Saved charts stay here even after you close the assistant.
              </p>
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => window.dispatchEvent(new CustomEvent('julio:open'))}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Ask Julio for a chart
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {customCharts.map((chart) => (
            <ConfiguredChart
              key={chart.id}
              config={chart.config}
              planKey={chart.planKey}
              title={chart.title}
              headerAction={(
                <button
                  type="button"
                  onClick={() => removeCustomChart(chart.id)}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              )}
            />
          ))}
        </div>
      )}
    </ChartSection>
  )
}

export default function Visualizations() {
  // Every chart renders the active plan – the one selected by the shared plan
  // chips. There is no per-chart diffing; the Summary and Employee tables carry
  // the cross-plan numbers.
  const comparing = useStore(isComparing)
  const activePlanKey = useStore(resolveActivePlanKey)
  const activePlan = useStore((state) => state[activePlanKey])
  const activeAnalysis = useStore((state) => state.analysis[activePlanKey])
  const planA = useStore((state) => state.planA)
  const planB = useStore((state) => state.planB)
  const planC = useStore((state) => state.planC)
  const planD = useStore((state) => state.planD)
  const analysisByPlan = useStore((state) => state.analysis)
  const chartData = activeAnalysis?.chartData
  const calculatedEmployees = activeAnalysis?.calculatedEmployees
  const turnover = activeAnalysis?.turnover
  const terminationHandling = activePlan.terminationHandling
  const { selectedGroup, pendingSelection, handleGroupSelect, viewPendingSelection, closeGroup, clearPending } = useChartGroupSelection()

  const employeesA = useMemo(
    () => getEmployeesForMetrics(calculatedEmployees || [], terminationHandling),
    [calculatedEmployees, terminationHandling]
  )
  const shownEmployees = employeesA
  const shownInclude = terminationHandling?.includeInMetrics
  const ownerSavingsCapacity = useMemo(
    () => buildOwnerSavingsCapacityData(shownEmployees, activePlan.planYear),
    [shownEmployees, activePlan.planYear]
  )
  const ownerPlanComparisons = useMemo(
    () => buildOwnerPlanComparisonData([
      { key: 'planA', plan: planA, analysis: analysisByPlan.planA },
      { key: 'planB', plan: planB, analysis: analysisByPlan.planB },
      { key: 'planC', plan: planC, analysis: analysisByPlan.planC },
      { key: 'planD', plan: planD, analysis: analysisByPlan.planD },
    ]),
    [planA, planB, planC, planD, analysisByPlan]
  )

  // Raw calculated employees for the shown plan, used by the selection dialog.
  const activeEmployees = calculatedEmployees

  const hasLoans = useMemo(
    () => (employeesA || []).some(e => e.loanBalance > 0),
    [employeesA]
  )

  if (!chartData || !calculatedEmployees || calculatedEmployees.length === 0) {
    return <NoDataMessage />
  }

  return (
    <div id="visualizations" className="space-y-6">
      <EmployeeGroupDialog
        group={selectedGroup}
        employees={activeEmployees || []}
        onClose={closeGroup}
      />
      <MultiSelectionBar
        selection={pendingSelection}
        onView={viewPendingSelection}
        onClear={clearPending}
      />
      <p className="text-xs text-muted-foreground">
        {comparing && 'Use the plan picker on any chart to switch all charts · '}
        Click a bar, point, or cell to see those employees · drag to box-select · hold Shift to combine selections.
      </p>
      <section id="visualizations-overview" className="scroll-mt-16 space-y-3">
        <h3 className="text-sm font-semibold tracking-tight">Overview charts</h3>
        <div className="space-y-4">
          <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,24rem),1fr))] gap-4">
            <ComparableCard
              id="chart-participation-status"
              title="Participation status"
              description="Distribution of employees by participation level"
              dataA={chartData.participationStatus}
            >
              {(data) => <ParticipationStatusChart data={data} onGroupSelect={handleGroupSelect} />}
            </ComparableCard>

            <ComparableCard
              id="chart-hce-nhce-comparison"
              title="HCE vs NHCE comparison"
              description="Key metrics comparison between highly compensated and non-highly compensated employees"
              dataA={chartData.hceNhceComparison}
            >
              {(data) => <HCEComparisonChart data={data} onGroupSelect={handleGroupSelect} />}
            </ComparableCard>
          </div>
          <div id="chart-overview-kpi-boxes" className="space-y-2">
            <OverviewInfoBoxes employees={shownEmployees} onGroupSelect={handleGroupSelect} />
          </div>
        </div>
      </section>

      <ChartSection id="visualizations-contributions" title="Contributions">
        <ComparableCard
          id="chart-age-contributions"
          title="Employee contributions by age band"
          description="Stacked breakdown of employee pre-tax, Roth, catch-up, and after-tax contributions"
          dataA={chartData.ageContributions}
        >
          {(data) => <AgeContributionsChart data={data} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>

        <ComparableCard
          id="chart-employer-by-age-band"
          title="Employer contributions by age band"
          description="Illustrative average employer contribution"
          dataA={chartData.employerByAgeBand}
        >
          {(data) => <EmployerByAgeBandChart data={data} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>
      </ChartSection>

      <ChartSection id="visualizations-distributions" title="Distributions">
        <ComparableCard
          id="chart-deferral-distribution"
          title="Deferral rate distribution"
          description="Number of employees by total deferral percentage"
          dataA={chartData.deferralDistribution}
        >
          {(data) => <DeferralDistributionChart data={data} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>

        <ComparableCard
          id="chart-compensation-distribution"
          title="Compensation distribution"
          description="Number of employees by compensation band"
          dataA={chartData.compensationDistribution}
        >
          {(data) => <CompensationDistributionChart data={data} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>

        <ComparableCard
          id="chart-catchup-usage"
          title="Catch-up usage"
          description="Eligibility and utilization of catch-up contributions"
          dataA={chartData.catchupUsage}
        >
          {(data) => <CatchupUsageChart data={data} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>
      </ChartSection>

      <ChartSection id="visualizations-relationships" title="Relationships">
        <ComparableCard
          id="chart-comp-vs-deferral"
          title="Compensation vs deferral rate"
          description="Relationship between pay and savings rate, colored by HCE status"
          dataA={chartData.compVsDeferral}
        >
          {(data) => <CompVsDeferralScatter data={data} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>

        <ComparableCard
          id="chart-age-vs-contribution"
          title="Age vs total contribution"
          description="Relationship between age and total employee contributions, colored by catch-up eligibility"
          dataA={chartData.ageVsContribution}
        >
          {(data) => <AgeVsContributionScatter data={data} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>
      </ChartSection>

      <TurnoverSection
        turnoverA={turnover}
        employeesA={calculatedEmployees}
        onGroupSelect={handleGroupSelect}
      />

      <ChartSection id="visualizations-demographics" title="Demographics">
        <ComparableCard
          id="chart-gender-pay-gap"
          title="Gender pay gap analysis"
          description="Comparison of compensation and contributions by gender"
          dataA={employeesA}
        >
          {(data) => <GenderPayGapChart employees={data} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>

        <ComparableCard
          id="chart-age-deferral-heatmap"
          title="Average deferral by age and gender"
          description="Heatmap showing average deferral percentage"
          dataA={employeesA}
        >
          {(data) => <AgeDeferralHeatmap employees={data} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>

        <ComparableCard
          id="chart-age-distribution"
          title="Age distribution"
          description="Employee count by age band"
          dataA={chartData.ageDistribution}
        >
          {(data) => <AgeDistributionChart data={data} includeInMetrics={shownInclude} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>

        <ComparableCard
          id="chart-tenure-distribution"
          title="Tenure distribution"
          description="Employee count by years of service"
          dataA={chartData.tenureDistribution}
        >
          {(data) => <TenureDistributionChart data={data} includeInMetrics={shownInclude} onGroupSelect={handleGroupSelect} />}
        </ComparableCard>
      </ChartSection>

      {hasLoans && (
        <ChartSection id="visualizations-loans" title="Loans">
          <ComparableCard
            id="chart-loan-takers-by-age"
            title="Who's taking loans"
            description="Employees with an outstanding loan by age group"
            dataA={chartData.loanByAgeBand}
          >
            {(data) => <LoanTakersByAgeChart data={data} onGroupSelect={handleGroupSelect} />}
          </ComparableCard>

          <ComparableCard
            id="chart-loan-amount-by-age"
            title="Loan amount by age band"
            description="Total loan balances grouped by employee age band"
            dataA={chartData.loanByAgeBand}
          >
            {(data) => <LoanByAgeBandChart data={data} onGroupSelect={handleGroupSelect} />}
          </ComparableCard>

          <ComparableCard
            id="chart-loan-vs-balance"
            title="Loan vs balance"
            description="Scatter plot of account balance vs loan balance"
            dataA={chartData.loanVsBalance}
          >
            {(data) => <LoanVsBalanceScatter data={data} onGroupSelect={handleGroupSelect} />}
          </ComparableCard>

          <ComparableCard
            id="chart-loan-distribution"
            title="Loan distribution"
            description="Number of employees by loan amount range"
            dataA={chartData.loanDistribution}
          >
            {(data) => <LoanDistributionChart data={data} onGroupSelect={handleGroupSelect} />}
          </ComparableCard>
        </ChartSection>
      )}

      <ChartSection
        id="visualizations-owner-savings"
        title="Owner Savings"
        description="See current statutory DC room and compare modeled allocations across calculated plan designs. Owners must be explicitly identified in Census."
        collapsible
        defaultCollapsed
      >
        <ChartCard
          id="chart-owner-savings-capacity"
          title="Owner savings capacity"
          description="Modeled DC annual additions and unused §415(c) room for each identified owner"
        >
          <OwnerSavingsCapacityChart data={ownerSavingsCapacity} planYear={activePlan.planYear} onGroupSelect={handleGroupSelect} />
        </ChartCard>
        <ChartCard
          id="chart-owner-plan-comparison"
          title="Owner allocation vs non-owner allocation"
          description="Calculated plan designs: owner retirement allocations compared with non-owner modeled employer allocations"
          showPlanSwitcher={false}
        >
          <OwnerPlanComparisonChart data={ownerPlanComparisons} activePlanKey={activePlanKey} />
        </ChartCard>
      </ChartSection>

      <CustomChartsSection />
    </div>
  )
}
